import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { formatToDDMonYYYY } from './dateUtils';
import * as XLSX from 'xlsx';
import './PMCalibrationTracker.css';
import { logDeletion, logUpload } from './auditLog';
import ReportUploadModal from './ReportUploadModal';
import SmartAssignModal from './SmartAssignModal';
import BulkUploadModal from './BulkUploadModal';

// Stable button component - uses DOM event delegation to avoid React re-renders
class StableButton extends React.Component {
  constructor(props) {
    super(props);
    this.buttonRef = React.createRef();
    this.handler = null;
  }
  
  componentDidMount() {
    this.attachHandler();
  }
  
  componentDidUpdate(prevProps) {
    // Re-attach handler if disabled state changes
    if (prevProps.disabled !== this.props.disabled) {
      this.detachHandler();
      this.attachHandler();
    }
  }
  
  componentWillUnmount() {
    this.detachHandler();
  }
  
  attachHandler() {
    const button = this.buttonRef.current;
    if (button && !this.props.disabled && !this.handler) {
      this.handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof this.props.onClick === 'function') {
          this.props.onClick();
        }
      };
      button.addEventListener('click', this.handler);
    }
  }
  
  detachHandler() {
    const button = this.buttonRef.current;
    if (button && this.handler) {
      button.removeEventListener('click', this.handler);
      this.handler = null;
    }
  }
  
  shouldComponentUpdate(nextProps) {
    // Only update if disabled state or children change
    return this.props.disabled !== nextProps.disabled ||
           this.props.children !== nextProps.children;
  }
  
  render() {
    const { className, title, children, disabled } = this.props;
    return (
      <button 
        ref={this.buttonRef}
        type="button"
        className={className}
        title={title}
        disabled={disabled}
        style={{ 
          minWidth: '180px', 
          contain: 'layout style paint',
          willChange: 'auto',
          pointerEvents: disabled ? 'none' : 'auto'
        }}
      >
        {children}
      </button>
    );
  }
}



// Stable Smart Assign button using DOM manipulation - completely outside React's render cycle
class StableSmartAssignButton extends React.Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.buttonRef = null;
    this.isLoadingRef = false;
    this.hasRendered = false;
    this.clickHandler = null;
  }
  
  componentDidMount() {
    // Only render if explicitly visible
    if (this.props.visible === true) {
      this.renderButton();
    }
  }
  
  shouldComponentUpdate(nextProps) {
    // Re-render if onClick reference changes or visibility changes
    return this.props.onClick !== nextProps.onClick || 
           this.props.visible !== nextProps.visible;
  }
  
  componentDidUpdate(prevProps) {
    // Handle visibility changes
    if (this.props.visible !== prevProps.visible) {
      if (this.props.visible === true) {
        this.renderButton();
      } else {
        this.clearButton();
      }
      return;
    }
    // Update button appearance without full re-render
    this.updateButtonAppearance();
  }
  
  componentWillUnmount() {
    this.clearButton();
  }
  
  clearButton() {
    const container = this.containerRef.current;
    if (container) {
      // Safe DOM clearing - remove all children instead of innerHTML
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    }
    this.buttonRef = null;
    this.hasRendered = false;
    this.clickHandler = null;
  }
  
  updateButtonAppearance() {
    const button = this.buttonRef;
    if (!button) return;
    
    const isLoading = this.props.isLoading;
    if (this.isLoadingRef === isLoading) return;
    this.isLoadingRef = isLoading;
    
    // Update all styles inline to maintain stability
    button.disabled = isLoading;
    button.style.cssText = `
      min-width: 180px !important;
      color: white !important;
      background-color: ${isLoading ? '#94a3b8' : '#22c55e'} !important;
      border: none !important;
      padding: 0.5rem 1rem !important;
      border-radius: 0.375rem !important;
      font-size: 0.875rem !important;
      font-weight: 500 !important;
      cursor: ${isLoading ? 'not-allowed' : 'pointer'} !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 0.5rem !important;
      opacity: ${isLoading ? '0.7' : '1'} !important;
      /* NUCLEAR Stability fixes for Chrome/Mobile */
      transition: none !important;
      transition-property: none !important;
      transition-duration: 0s !important;
      transition-timing-function: step-start !important;
      transition-delay: 0s !important;
      transform: none !important;
      -webkit-transform: translateZ(0) !important;
      -moz-transform: none !important;
      -ms-transform: none !important;
      will-change: auto !important;
      backface-visibility: hidden !important;
      -webkit-backface-visibility: hidden !important;
      contain: layout style paint !important;
      animation: none !important;
      -webkit-animation: none !important;
      box-shadow: none !important;
      -webkit-box-shadow: none !important;
      outline: none !important;
      -webkit-tap-highlight-color: transparent !important;
      user-select: none !important;
      -webkit-user-select: none !important;
    `;
    button.textContent = isLoading ? '⏳ Loading...' : '🎯 Smart Multi-Assign';
    
    // Update click handler - remove old listener and add new one
    if (this.clickHandler) {
      button.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }
    if (!isLoading && this.props.onClick) {
      this.clickHandler = this.props.onClick;
      button.addEventListener('click', this.clickHandler);
    }
  }
  
  renderButton() {
    // Double-check visibility before rendering
    if (this.props.visible !== true) return;
    
    const container = this.containerRef.current;
    if (!container) return;
    
    // Prevent duplicate renders
    if (this.hasRendered && this.buttonRef) return;
    
    // Clear previous content safely
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    
    // Create button with inline styles for maximum stability
    const button = document.createElement('button');
    button.type = 'button';
    // Use a unique class to avoid CSS conflicts
    button.className = 'smart-assign-btn';
    
    // Set initial state
    const isLoading = this.props.isLoading;
    
    // NUCLEAR OPTION: Apply ALL styles inline to prevent CSS transition issues
    button.style.cssText = `
      min-width: 180px !important;
      color: white !important;
      background-color: ${isLoading ? '#94a3b8' : '#22c55e'} !important;
      border: none !important;
      padding: 0.5rem 1rem !important;
      border-radius: 0.375rem !important;
      font-size: 0.875rem !important;
      font-weight: 500 !important;
      cursor: ${isLoading ? 'not-allowed' : 'pointer'} !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 0.5rem !important;
      opacity: ${isLoading ? '0.7' : '1'} !important;
      /* NUCLEAR Stability fixes for Chrome/Mobile - prevent ALL transitions */
      transition: none !important;
      transition-property: none !important;
      transition-duration: 0s !important;
      transition-timing-function: step-start !important;
      transition-delay: 0s !important;
      transform: none !important;
      -webkit-transform: translateZ(0) !important;
      -moz-transform: none !important;
      -ms-transform: none !important;
      will-change: auto !important;
      backface-visibility: hidden !important;
      -webkit-backface-visibility: hidden !important;
      contain: layout style paint !important;
      animation: none !important;
      -webkit-animation: none !important;
      box-shadow: none !important;
      -webkit-box-shadow: none !important;
      outline: none !important;
      -webkit-tap-highlight-color: transparent !important;
      user-select: none !important;
      -webkit-user-select: none !important;
    `;
    
    button.disabled = isLoading;
    button.textContent = isLoading ? '⏳ Loading...' : '🎯 Smart Multi-Assign';
    
    // Use addEventListener instead of onclick for better stability
    if (!isLoading && this.props.onClick) {
      this.clickHandler = this.props.onClick;
      button.addEventListener('click', this.clickHandler);
    }
    
    container.appendChild(button);
    this.buttonRef = button;
    this.isLoadingRef = isLoading;
    this.hasRendered = true;
  }
  
  render() {
    // Only render container if visible
    if (this.props.visible !== true) {
      return null;
    }
    return React.createElement('div', {
      ref: this.containerRef,
      className: 'smart-assign-container',
      style: { display: 'flex' }
    });
  }
}

// Stable Bulk Upload button using DOM manipulation - completely outside React's render cycle
class StableBulkUploadButton extends React.Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.buttonRef = null;
    this.isLoadingRef = false;
    this.hasRendered = false;
    this.clickHandler = null;
  }
  
  componentDidMount() {
    // Only render if explicitly visible
    if (this.props.visible === true) {
      this.renderButton();
    }
  }
  
  shouldComponentUpdate(nextProps) {
    // Re-render if onClick reference changes or visibility changes
    return this.props.onClick !== nextProps.onClick || 
           this.props.visible !== nextProps.visible;
  }
  
  componentDidUpdate(prevProps) {
    // Handle visibility changes
    if (this.props.visible !== prevProps.visible) {
      if (this.props.visible === true) {
        this.renderButton();
      } else {
        this.clearButton();
      }
      return;
    }
    // Update button appearance without full re-render
    this.updateButtonAppearance();
  }
  
  componentWillUnmount() {
    this.clearButton();
  }
  
  clearButton() {
    const container = this.containerRef.current;
    if (container) {
      // Safe DOM clearing - remove all children instead of innerHTML
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    }
    this.buttonRef = null;
    this.hasRendered = false;
    this.clickHandler = null;
  }
  
  updateButtonAppearance() {
    const button = this.buttonRef;
    if (!button) return;
    
    const isLoading = this.props.isLoading;
    if (this.isLoadingRef === isLoading) return;
    this.isLoadingRef = isLoading;
    
    // Update all styles inline to maintain stability
    button.disabled = isLoading;
    button.style.cssText = `
      min-width: 180px !important;
      color: white !important;
      background-color: ${isLoading ? '#94a3b8' : '#3b82f6'} !important;
      border: none !important;
      padding: 0.5rem 1rem !important;
      border-radius: 0.375rem !important;
      font-size: 0.875rem !important;
      font-weight: 500 !important;
      cursor: ${isLoading ? 'not-allowed' : 'pointer'} !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 0.5rem !important;
      opacity: ${isLoading ? '0.7' : '1'} !important;
      /* NUCLEAR Stability fixes for Chrome/Mobile */
      transition: none !important;
      transition-property: none !important;
      transition-duration: 0s !important;
      transition-timing-function: step-start !important;
      transition-delay: 0s !important;
      transform: none !important;
      -webkit-transform: translateZ(0) !important;
      -moz-transform: none !important;
      -ms-transform: none !important;
      will-change: auto !important;
      backface-visibility: hidden !important;
      -webkit-backface-visibility: hidden !important;
      contain: layout style paint !important;
      animation: none !important;
      -webkit-animation: none !important;
      box-shadow: none !important;
      -webkit-box-shadow: none !important;
      outline: none !important;
      -webkit-tap-highlight-color: transparent !important;
      user-select: none !important;
      -webkit-user-select: none !important;
    `;
    button.textContent = isLoading ? '⏳ Loading...' : '📁 Bulk Upload';
    
    // Update click handler - remove old listener and add new one
    if (this.clickHandler) {
      button.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }
    if (!isLoading && this.props.onClick) {
      this.clickHandler = this.props.onClick;
      button.addEventListener('click', this.clickHandler);
    }
  }
  
  renderButton() {
    // Double-check visibility before rendering
    if (this.props.visible !== true) return;
    
    const container = this.containerRef.current;
    if (!container) return;
    
    // Prevent duplicate renders
    if (this.hasRendered && this.buttonRef) return;
    
    // Clear previous content safely
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    
    // Create button with inline styles for maximum stability
    const button = document.createElement('button');
    button.type = 'button';
    // Use a unique class to avoid CSS conflicts
    button.className = 'bulk-upload-btn-stable';
    
    // Set initial state
    const isLoading = this.props.isLoading;
    
    // NUCLEAR OPTION: Apply ALL styles inline to prevent CSS transition issues
    button.style.cssText = `
      min-width: 180px !important;
      color: white !important;
      background-color: ${isLoading ? '#94a3b8' : '#3b82f6'} !important;
      border: none !important;
      padding: 0.5rem 1rem !important;
      border-radius: 0.375rem !important;
      font-size: 0.875rem !important;
      font-weight: 500 !important;
      cursor: ${isLoading ? 'not-allowed' : 'pointer'} !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 0.5rem !important;
      opacity: ${isLoading ? '0.7' : '1'} !important;
      /* NUCLEAR Stability fixes for Chrome/Mobile - prevent ALL transitions */
      transition: none !important;
      transition-property: none !important;
      transition-duration: 0s !important;
      transition-timing-function: step-start !important;
      transition-delay: 0s !important;
      transform: none !important;
      -webkit-transform: translateZ(0) !important;
      -moz-transform: none !important;
      -ms-transform: none !important;
      will-change: auto !important;
      backface-visibility: hidden !important;
      -webkit-backface-visibility: hidden !important;
      contain: layout style paint !important;
      animation: none !important;
      -webkit-animation: none !important;
      box-shadow: none !important;
      -webkit-box-shadow: none !important;
      outline: none !important;
      -webkit-tap-highlight-color: transparent !important;
      user-select: none !important;
      -webkit-user-select: none !important;
    `;
    
    button.disabled = isLoading;
    button.textContent = isLoading ? '⏳ Loading...' : '📁 Bulk Upload';
    
    // Use addEventListener instead of onclick for better stability
    if (!isLoading && this.props.onClick) {
      this.clickHandler = this.props.onClick;
      button.addEventListener('click', this.clickHandler);
    }
    
    container.appendChild(button);
    this.buttonRef = button;
    this.isLoadingRef = isLoading;
    this.hasRendered = true;
  }
  
  render() {
    // Only render container if visible
    if (this.props.visible !== true) {
      return null;
    }
    return React.createElement('div', {
      ref: this.containerRef,
      className: 'bulk-upload-container',
      style: { display: 'flex' }
    });
  }
}

const PMCalibrationTracker = ({ currentUser }) => {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [equipmentNameFilter, setEquipmentNameFilter] = useState('');
  const [maintenanceTypeFilter, setMaintenanceTypeFilter] = useState('all'); // PM, Calibration, or All
  const [statusFilter, setStatusFilter] = useState('all'); // Due or Done
  
  // Excel-style column filters
  const [columnFilters, setColumnFilters] = useState({
    department: '',
    equipmentName: '',
    assetId: '',
    serialNo: '',
    pmDue: { start: '', end: '' },      // Date range
    pmDone: { start: '', end: '' },     // Date range
    calDue: { start: '', end: '' },     // Date range
    calDone: { start: '', end: '' }     // Date range
  });
  
  // Get unique values for dropdown filters from FILTERED assets (not all assets)
  const getUniqueValues = (fieldName) => {
    // First apply all filters EXCEPT column filters to get base filtered data
    let baseFiltered = [...assets];
    
    // Filter out condemned and shifted assets (only show active and biomedical assets)
    baseFiltered = baseFiltered.filter(asset => {
      if (!asset || typeof asset !== 'object') return false;
      const status = asset.status || 'active';
      return status !== 'condemned' && status !== 'shifted';
    });
    
    // Apply equipment name filter
    if (equipmentNameFilter.trim() !== '') {
      const term = equipmentNameFilter.toLowerCase().trim();
      baseFiltered = baseFiltered.filter(asset => {
        if (!asset || typeof asset !== 'object') return false;
        const equipmentName = safeToString(asset.equipmentName).toLowerCase();
        return equipmentName.includes(term);
      });
    }
    
    // Apply general search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      baseFiltered = baseFiltered.filter(asset => {
        if (!asset || typeof asset !== 'object') return false;
        const department = safeToString(asset.department).toLowerCase();
        const equipmentName = safeToString(asset.equipmentName).toLowerCase();
        const assetId = safeToString(asset.assetId).toLowerCase();
        const serialNo = safeToString(asset.serialNo).toLowerCase();
        const manufacturer = safeToString(asset.manufacturer).toLowerCase();
        const model = safeToString(asset.model).toLowerCase();
        const type = safeToString(asset.type).toLowerCase();
        return (
          department.includes(term) ||
          equipmentName.includes(term) ||
          assetId.includes(term) ||
          serialNo.includes(term) ||
          manufacturer.includes(term) ||
          model.includes(term) ||
          type.includes(term)
        );
      });
    }
    
    // Apply maintenance type filter
    if (maintenanceTypeFilter !== 'all') {
      baseFiltered = baseFiltered.filter(asset => {
        if (!asset || typeof asset !== 'object') return false;
        const hasPM = asset.pmDue || asset.pmDone;
        const hasCal = asset.calDue || asset.calDone;
        if (maintenanceTypeFilter === 'pm') return hasPM;
        if (maintenanceTypeFilter === 'calibration') return hasCal;
        if (maintenanceTypeFilter === 'both') return hasPM && hasCal;
        return true;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      baseFiltered = baseFiltered.filter(asset => {
        if (!asset || typeof asset !== 'object') return false;
        if (statusFilter === 'due') {
          if (maintenanceTypeFilter === 'pm') {
            const pmDueDate = parseDate(asset.pmDue);
            return pmDueDate && pmDueDate >= today;
          } else if (maintenanceTypeFilter === 'calibration') {
            const calDueDate = parseDate(asset.calDue);
            return calDueDate && calDueDate >= today;
          } else {
            const pmDueDate = parseDate(asset.pmDue);
            const calDueDate = parseDate(asset.calDue);
            return (pmDueDate && pmDueDate >= today) || (calDueDate && calDueDate >= today);
          }
        } else if (statusFilter === 'done') {
          if (maintenanceTypeFilter === 'pm') return asset.pmDone;
          if (maintenanceTypeFilter === 'calibration') return asset.calDone;
          return asset.pmDone || asset.calDone;
        }
        return true;
      });
    }
    
    // Apply date filter (This Month Done, This Month Due, etc.)
    if (dateFilter !== 'all' && (!dateRangeFilter.startDate && !dateRangeFilter.endDate)) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      baseFiltered = baseFiltered.filter(asset => {
        if (!asset || typeof asset !== 'object') return false;
        try {
          if (dateFilter === 'dueToday') {
            const pmDueDate = parseDate(asset.pmDue);
            const calDueDate = parseDate(asset.calDue);
            if (pmDueDate) {
              pmDueDate.setHours(0, 0, 0, 0);
              if (pmDueDate.getTime() === today.getTime()) return true;
            }
            if (calDueDate) {
              calDueDate.setHours(0, 0, 0, 0);
              if (calDueDate.getTime() === today.getTime()) return true;
            }
            return false;
          } else if (dateFilter === 'thisMonthDone') {
            const pmDoneDate = parseDate(asset.pmDone);
            const calDoneDate = parseDate(asset.calDone);
            if (pmDoneDate && pmDoneDate >= firstDayOfMonth && pmDoneDate <= lastDayOfMonth) return true;
            if (calDoneDate && calDoneDate >= firstDayOfMonth && calDoneDate <= lastDayOfMonth) return true;
            return false;
          } else if (dateFilter === 'thisMonthDue') {
            const pmDueDate = parseDate(asset.pmDue);
            const calDueDate = parseDate(asset.calDue);
            if (pmDueDate && pmDueDate >= firstDayOfMonth && pmDueDate <= lastDayOfMonth) return true;
            if (calDueDate && calDueDate >= firstDayOfMonth && calDueDate <= lastDayOfMonth) return true;
            return false;
          }
        } catch (dateError) {
          console.warn('Error in getUniqueValues date filter:', dateError);
        }
        return dateFilter === 'all';
      });
    }
    
    // Now get unique values from the filtered data
    const values = baseFiltered
      .map(asset => {
        const value = asset[fieldName];
        // Format dates for display
        if (fieldName === 'pmDue' || fieldName === 'pmDone' || fieldName === 'calDue' || fieldName === 'calDone') {
          return formatAssetDate(value);
        }
        return safeToString(value);
      })
      .filter(value => value && value.trim() !== '' && value !== 'NR' && value !== 'NA')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return values;
  };
  
  // Date range filter states
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: '',
    endDate: '',
    type: 'all' // all, due, done
  });
  
  // Bulk edit states
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [bulkEditForm, setBulkEditForm] = useState({
    pmDue: '',
    pmDone: '',
    calDue: '',
    calDone: ''
  });
  
  // Editing states
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({
    pmDue: '',
    pmDone: '',
    calDue: '',
    calDone: ''
  });

  // Report upload states
  const [showReportUpload, setShowReportUpload] = useState(false);
  const [selectedAssetForReport, setSelectedAssetForReport] = useState(null);
  const [reportUploadForm, setReportUploadForm] = useState({
    pmReportFile: null,
    pmReportPreview: '',
    pmReportPages: [], // Multi-page support
    calReportFile: null,
    calReportPreview: '',
    calReportPages: [] // Multi-page support
  });
  const [showCamera, setShowCamera] = useState(false);
  const [cameraForType, setCameraForType] = useState(null); // 'pm' or 'cal'
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null); // Ref to track stream for callbacks
  const [cameraPreview, setCameraPreview] = useState(null); // For preview before saving
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const pmFileInputRef = useRef(null);
  const calFileInputRef = useRef(null);
  
  // Keep streamRef in sync with stream state
  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  // View documents modal state
  const [showViewDocuments, setShowViewDocuments] = useState(false);
  const [selectedAssetForView, setSelectedAssetForView] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentReportType, setCurrentReportType] = useState('pm'); // 'pm' or 'cal'
  const [imageLoading, setImageLoading] = useState(true);
  
  // Date editing state for View Documents modal
  const [editingDates, setEditingDates] = useState({
    pmDone: '',
    pmDue: '',
    calDone: '',
    calDue: ''
  });
  const [dateSaveMessage, setDateSaveMessage] = useState('');
  const [imageError, setImageError] = useState(false);
  
  // Report history modal state
  const [showReportHistory, setShowReportHistory] = useState(false);
  const [reportHistoryData, setReportHistoryData] = useState([]);

  // Bulk report upload states
  const [showBulkReportUpload, setShowBulkReportUpload] = useState(false);
  const [bulkReportFiles, setBulkReportFiles] = useState([]);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ current: 0, total: 0, matched: 0, unmatched: [] });
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadSuccess, setBulkUploadSuccess] = useState(null); // { matched: number, unmatched: number }

  // Smart multi-equipment assignment states
  const [showSmartAssign, setShowSmartAssign] = useState(false);
  const [smartAssignForm, setSmartAssignForm] = useState({
    equipmentType: '',
    reportType: 'pm', // 'pm' or 'cal'
    reportFile: null,
    reportPreview: '',
    reportPages: [],
    matchedAssets: [],
    assignToAll: false
  });
  
  // Bulk Upload modal state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [isOpeningBulkUpload, setIsOpeningBulkUpload] = useState(false);

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [assets, dateFilter, searchTerm, equipmentNameFilter, maintenanceTypeFilter, statusFilter, dateRangeFilter, columnFilters]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/assets');
      if (!response.ok) {
        throw new Error(`Failed to load assets (${response.status})`);
      }

      const payload = await response.json();
      const parsedAssets = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data : []);
      const validAssets = parsedAssets.filter(asset => asset && typeof asset === 'object');
      setAssets(validAssets);
      setFilteredAssets(validAssets);
    } catch (err) {
      console.error('Error loading assets:', err);
      setError('Failed to load assets from server: ' + err.message);
      setAssets([]);
      setFilteredAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const saveAssets = (newAssets) => {
    try {
      setAssets(newAssets);

      fetch('/api/assets/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assets: newAssets })
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to save assets (${response.status})`);
        }
      }).catch((error) => {
        console.error('Failed to save assets to server:', error);
        setError('Failed to save assets to server: ' + error.message);
      });

      return true;
    } catch (error) {
      console.error('Failed to save assets:', error);
      setError('Failed to save assets: ' + error.message);
      return false;
    }
  };

  // Safely convert any value to string for searching, but preserve numbers for date formatting
  const safeToString = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString();
    }
    // For objects and arrays, use JSON string representation
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '';
    }
  };

  // Enhanced date formatting that handles Excel serial dates
  const formatAssetDate = (dateValue) => {
    // Don't convert to string first - pass the original value to formatToDDMonYYYY
    // This allows the function to properly handle Excel serial dates as numbers
    if (dateValue === null || dateValue === undefined || dateValue === '' || 
        (typeof dateValue === 'string' && dateValue.trim() === '')) {
      return '';
    }
    
    // Use the existing formatToDDMonYYYY utility which already handles Excel serial dates
    return formatToDDMonYYYY(dateValue);
  };

  const applyFilters = () => {
    try {
      setError(null); // Clear any previous errors
      let filtered = [...assets];
      
      // Apply column filters (partial match)
      if (columnFilters.department) {
        filtered = filtered.filter(asset => 
          safeToString(asset.department).toLowerCase().includes(columnFilters.department.toLowerCase())
        );
      }
      if (columnFilters.equipmentName) {
        filtered = filtered.filter(asset => 
          safeToString(asset.equipmentName).toLowerCase().includes(columnFilters.equipmentName.toLowerCase())
        );
      }
      if (columnFilters.assetId) {
        filtered = filtered.filter(asset => 
          safeToString(asset.assetId).toLowerCase().includes(columnFilters.assetId.toLowerCase())
        );
      }
      if (columnFilters.serialNo) {
        filtered = filtered.filter(asset => 
          safeToString(asset.serialNo).toLowerCase().includes(columnFilters.serialNo.toLowerCase())
        );
      }
      // Date filters support date range
      if (columnFilters.pmDue.start || columnFilters.pmDue.end) {
        filtered = filtered.filter(asset => {
          const assetDate = parseDate(asset.pmDue);
          if (!assetDate) return false;
          
          const startDate = columnFilters.pmDue.start ? new Date(columnFilters.pmDue.start) : null;
          const endDate = columnFilters.pmDue.end ? new Date(columnFilters.pmDue.end) : null;
          
          if (startDate && endDate) {
            return assetDate >= startDate && assetDate <= endDate;
          } else if (startDate) {
            return assetDate >= startDate;
          } else if (endDate) {
            return assetDate <= endDate;
          }
          return true;
        });
      }
      if (columnFilters.pmDone.start || columnFilters.pmDone.end) {
        filtered = filtered.filter(asset => {
          const assetDate = parseDate(asset.pmDone);
          if (!assetDate) return false;
          
          const startDate = columnFilters.pmDone.start ? new Date(columnFilters.pmDone.start) : null;
          const endDate = columnFilters.pmDone.end ? new Date(columnFilters.pmDone.end) : null;
          
          if (startDate && endDate) {
            return assetDate >= startDate && assetDate <= endDate;
          } else if (startDate) {
            return assetDate >= startDate;
          } else if (endDate) {
            return assetDate <= endDate;
          }
          return true;
        });
      }
      if (columnFilters.calDue.start || columnFilters.calDue.end) {
        filtered = filtered.filter(asset => {
          const assetDate = parseDate(asset.calDue);
          if (!assetDate) return false;
          
          const startDate = columnFilters.calDue.start ? new Date(columnFilters.calDue.start) : null;
          const endDate = columnFilters.calDue.end ? new Date(columnFilters.calDue.end) : null;
          
          if (startDate && endDate) {
            return assetDate >= startDate && assetDate <= endDate;
          } else if (startDate) {
            return assetDate >= startDate;
          } else if (endDate) {
            return assetDate <= endDate;
          }
          return true;
        });
      }
      if (columnFilters.calDone.start || columnFilters.calDone.end) {
        filtered = filtered.filter(asset => {
          const assetDate = parseDate(asset.calDone);
          if (!assetDate) return false;
          
          const startDate = columnFilters.calDone.start ? new Date(columnFilters.calDone.start) : null;
          const endDate = columnFilters.calDone.end ? new Date(columnFilters.calDone.end) : null;
          
          if (startDate && endDate) {
            return assetDate >= startDate && assetDate <= endDate;
          } else if (startDate) {
            return assetDate >= startDate;
          } else if (endDate) {
            return assetDate <= endDate;
          }
          return true;
        });
      }
      
      // Filter out condemned and shifted assets (only show active and biomedical assets)
      filtered = filtered.filter(asset => {
        if (!asset || typeof asset !== 'object') return false;
        const status = asset.status || 'active';
        return status !== 'condemned' && status !== 'shifted';
      });
      
      // Apply equipment name filter (specific)
      if (equipmentNameFilter.trim() !== '') {
        const term = equipmentNameFilter.toLowerCase().trim();
        filtered = filtered.filter(asset => {
          if (!asset || typeof asset !== 'object') return false;
          const equipmentName = safeToString(asset.equipmentName).toLowerCase();
          return equipmentName.includes(term);
        });
      }
      
      // Apply general search filter - dynamic search across all fields
      if (searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(asset => {
          if (!asset || typeof asset !== 'object') return false;
          
          // Safely convert all fields to strings for searching
          const department = safeToString(asset.department).toLowerCase();
          const equipmentName = safeToString(asset.equipmentName).toLowerCase();
          const assetId = safeToString(asset.assetId).toLowerCase();
          const serialNo = safeToString(asset.serialNo).toLowerCase();
          const floor = safeToString(asset.floor).toLowerCase();
          const manufacturer = safeToString(asset.manufacturer).toLowerCase();
          const model = safeToString(asset.model).toLowerCase();
          const type = safeToString(asset.type).toLowerCase();
          const pmDue = formatAssetDate(asset.pmDue).toLowerCase();
          const pmDone = formatAssetDate(asset.pmDone).toLowerCase();
          const calDue = formatAssetDate(asset.calDue).toLowerCase();
          const calDone = formatAssetDate(asset.calDone).toLowerCase();
          
          // Search across all fields
          return (
            department.includes(term) ||
            equipmentName.includes(term) ||
            assetId.includes(term) ||
            serialNo.includes(term) ||
            floor.includes(term) ||
            manufacturer.includes(term) ||
            model.includes(term) ||
            type.includes(term) ||
            pmDue.includes(term) ||
            pmDone.includes(term) ||
            calDue.includes(term) ||
            calDone.includes(term)
          );
        });
      }
      
      // Apply date range filter
      if (dateRangeFilter.startDate || dateRangeFilter.endDate) {
        const startDate = dateRangeFilter.startDate ? new Date(dateRangeFilter.startDate) : null;
        const endDate = dateRangeFilter.endDate ? new Date(dateRangeFilter.endDate) : null;
        
        if (startDate) {
          startDate.setHours(0, 0, 0, 0);
        }
        if (endDate) {
          endDate.setHours(23, 59, 59, 999);
        }
        
        filtered = filtered.filter(asset => {
          if (!asset || typeof asset !== 'object') return false;
          
          try {
            // Check PM Due date
            const pmDueDate = parseDate(asset.pmDue);
            // Check PM Done date
            const pmDoneDate = parseDate(asset.pmDone);
            // Check Cal Due date
            const calDueDate = parseDate(asset.calDue);
            // Check Cal Done date
            const calDoneDate = parseDate(asset.calDone);
            
            // Apply filter based on type
            if (dateRangeFilter.type === 'due') {
              // Check if any due date is within range
              if (pmDueDate && isDateInRange(pmDueDate, startDate, endDate)) return true;
              if (calDueDate && isDateInRange(calDueDate, startDate, endDate)) return true;
              return false;
            } else if (dateRangeFilter.type === 'done') {
              // Check if any done date is within range
              if (pmDoneDate && isDateInRange(pmDoneDate, startDate, endDate)) return true;
              if (calDoneDate && isDateInRange(calDoneDate, startDate, endDate)) return true;
              return false;
            } else {
              // Check if any date is within range (all dates)
              if (pmDueDate && isDateInRange(pmDueDate, startDate, endDate)) return true;
              if (pmDoneDate && isDateInRange(pmDoneDate, startDate, endDate)) return true;
              if (calDueDate && isDateInRange(calDueDate, startDate, endDate)) return true;
              if (calDoneDate && isDateInRange(calDoneDate, startDate, endDate)) return true;
              return false;
            }
          } catch (dateError) {
            console.warn('Error processing date range filter for asset:', asset, dateError);
            return false;
          }
        });
      }
      
      // Apply maintenance type filter (PM, Calibration, or Both)
      if (maintenanceTypeFilter !== 'all') {
        filtered = filtered.filter(asset => {
          if (!asset || typeof asset !== 'object') return false;
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const hasPM = asset.pmDue || asset.pmDone;
          const hasCal = asset.calDue || asset.calDone;
          
          if (maintenanceTypeFilter === 'pm') {
            // Show assets with PM dates only
            return hasPM;
          } else if (maintenanceTypeFilter === 'calibration') {
            // Show assets with Calibration dates only
            return hasCal;
          } else if (maintenanceTypeFilter === 'both') {
            // Show assets with BOTH PM and Calibration dates
            return hasPM && hasCal;
          }
          
          return true;
        });
      }
      
      // Apply status filter (Due or Done)
      if (statusFilter !== 'all') {
        filtered = filtered.filter(asset => {
          if (!asset || typeof asset !== 'object') return false;
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (statusFilter === 'due') {
            // Show assets with upcoming due dates
            if (maintenanceTypeFilter === 'pm') {
              const pmDueDate = parseDate(asset.pmDue);
              return pmDueDate && pmDueDate >= today;
            } else if (maintenanceTypeFilter === 'calibration') {
              const calDueDate = parseDate(asset.calDue);
              return calDueDate && calDueDate >= today;
            } else if (maintenanceTypeFilter === 'both') {
              // For 'both' filter, require both PM and Cal to be due
              const pmDueDate = parseDate(asset.pmDue);
              const calDueDate = parseDate(asset.calDue);
              return (pmDueDate && pmDueDate >= today) || (calDueDate && calDueDate >= today);
            } else {
              // All - show any due dates
              const pmDueDate = parseDate(asset.pmDue);
              const calDueDate = parseDate(asset.calDue);
              return (pmDueDate && pmDueDate >= today) || (calDueDate && calDueDate >= today);
            }
          } else if (statusFilter === 'done') {
            // Show assets with done dates
            if (maintenanceTypeFilter === 'pm') {
              return asset.pmDone;
            } else if (maintenanceTypeFilter === 'calibration') {
              return asset.calDone;
            } else if (maintenanceTypeFilter === 'both') {
              // For 'both' filter, show if either has done date
              return asset.pmDone || asset.calDone;
            } else {
              // All - show any done dates
              return asset.pmDone || asset.calDone;
            }
          }
          
          return true;
        });
      }
      
      // Apply date filter (respects maintenance type filter)
      if (dateFilter !== 'all' && (!dateRangeFilter.startDate && !dateRangeFilter.endDate)) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        filtered = filtered.filter(asset => {
          if (!asset || typeof asset !== 'object') return false;
          
          try {
            if (dateFilter === 'dueToday') {
              // Check based on maintenance type filter
              if (maintenanceTypeFilter === 'pm') {
                // Only check PM Due
                const pmDueDate = parseDate(asset.pmDue);
                if (pmDueDate) {
                  pmDueDate.setHours(0, 0, 0, 0);
                  return pmDueDate.getTime() === today.getTime();
                }
                return false;
              } else if (maintenanceTypeFilter === 'calibration') {
                // Only check Calibration Due
                const calDueDate = parseDate(asset.calDue);
                if (calDueDate) {
                  calDueDate.setHours(0, 0, 0, 0);
                  return calDueDate.getTime() === today.getTime();
                }
                return false;
              } else if (maintenanceTypeFilter === 'both') {
                // For 'both' filter, check if either is due today
                const pmDueDate = parseDate(asset.pmDue);
                const calDueDate = parseDate(asset.calDue);
                
                if (pmDueDate) {
                  pmDueDate.setHours(0, 0, 0, 0);
                  if (pmDueDate.getTime() === today.getTime()) return true;
                }
                if (calDueDate) {
                  calDueDate.setHours(0, 0, 0, 0);
                  if (calDueDate.getTime() === today.getTime()) return true;
                }
                return false;
              } else {
                // Check both PM and Calibration
                const pmDueDate = parseDate(asset.pmDue);
                if (pmDueDate) {
                  pmDueDate.setHours(0, 0, 0, 0);
                  if (pmDueDate.getTime() === today.getTime()) return true;
                }
                
                const calDueDate = parseDate(asset.calDue);
                if (calDueDate) {
                  calDueDate.setHours(0, 0, 0, 0);
                  if (calDueDate.getTime() === today.getTime()) return true;
                }
                return false;
              }
            } else if (dateFilter === 'thisMonthDone') {
              // Check based on maintenance type filter
              if (maintenanceTypeFilter === 'pm') {
                // Only check PM Done
                const pmDoneDate = parseDate(asset.pmDone);
                return pmDoneDate && pmDoneDate >= firstDayOfMonth && pmDoneDate <= lastDayOfMonth;
              } else if (maintenanceTypeFilter === 'calibration') {
                // Only check Calibration Done
                const calDoneDate = parseDate(asset.calDone);
                return calDoneDate && calDoneDate >= firstDayOfMonth && calDoneDate <= lastDayOfMonth;
              } else if (maintenanceTypeFilter === 'both') {
                // For 'both' filter, check if either is done this month
                const pmDoneDate = parseDate(asset.pmDone);
                const calDoneDate = parseDate(asset.calDone);
                
                if (pmDoneDate && pmDoneDate >= firstDayOfMonth && pmDoneDate <= lastDayOfMonth) return true;
                if (calDoneDate && calDoneDate >= firstDayOfMonth && calDoneDate <= lastDayOfMonth) return true;
                
                return false;
              } else {
                // Check both PM and Calibration Done
                const pmDoneDate = parseDate(asset.pmDone);
                const calDoneDate = parseDate(asset.calDone);
                
                if (pmDoneDate && pmDoneDate >= firstDayOfMonth && pmDoneDate <= lastDayOfMonth) return true;
                if (calDoneDate && calDoneDate >= firstDayOfMonth && calDoneDate <= lastDayOfMonth) return true;
                
                return false;
              }
            } else if (dateFilter === 'thisMonthDue') {
              // Check based on maintenance type filter
              if (maintenanceTypeFilter === 'pm') {
                // Only check PM Due
                const pmDueDate = parseDate(asset.pmDue);
                return pmDueDate && pmDueDate >= firstDayOfMonth && pmDueDate <= lastDayOfMonth;
              } else if (maintenanceTypeFilter === 'calibration') {
                // Only check Calibration Due
                const calDueDate = parseDate(asset.calDue);
                return calDueDate && calDueDate >= firstDayOfMonth && calDueDate <= lastDayOfMonth;
              } else if (maintenanceTypeFilter === 'both') {
                // For 'both' filter, check if either is due this month
                const pmDueDate = parseDate(asset.pmDue);
                const calDueDate = parseDate(asset.calDue);
                
                if (pmDueDate && pmDueDate >= firstDayOfMonth && pmDueDate <= lastDayOfMonth) return true;
                if (calDueDate && calDueDate >= firstDayOfMonth && calDueDate <= lastDayOfMonth) return true;
                
                return false;
              } else {
                // Check both PM and Calibration Due
                const pmDueDate = parseDate(asset.pmDue);
                const calDueDate = parseDate(asset.calDue);
                
                if (pmDueDate && pmDueDate >= firstDayOfMonth && pmDueDate <= lastDayOfMonth) return true;
                if (calDueDate && calDueDate >= firstDayOfMonth && calDueDate <= lastDayOfMonth) return true;
                
                return false;
              }
            }
          } catch (dateError) {
            console.warn('Error processing date filter for asset:', asset, dateError);
            // Continue with other assets even if one has date parsing issues
          }
          
          return dateFilter === 'all'; // If no specific date filter matches, include only if filter is 'all'
        });
      }
      
      setFilteredAssets(filtered);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters: ' + err.message);
      // Still set filtered assets to avoid breaking the UI
      setFilteredAssets([...assets]);
    }
  };

  // Check if a date is within the specified range
  const isDateInRange = (date, startDate, endDate) => {
    if (!date) return false;
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    if (startDate && endDate) {
      return checkDate >= startDate && checkDate <= endDate;
    } else if (startDate) {
      return checkDate >= startDate;
    } else if (endDate) {
      return checkDate <= endDate;
    }
    return false;
  };

  // Parse date helper function - enhanced for DD-Mon-YY format and Excel serial dates
  const parseDate = (dateString) => {
    // Don't convert to string first - pass the original value to formatToDDMonYYYY
    if (dateString === null || dateString === undefined || dateString === '' || 
        (typeof dateString === 'string' && dateString.trim() === '')) {
      return null;
    }
    
    try {
      // Use the existing date formatting utility which already handles Excel serial dates
      const formattedDate = formatToDDMonYYYY(dateString);
      if (formattedDate && formattedDate !== dateString) {
        // If formatToDDMonYYYY changed the date, it was an Excel serial date
        // Parse the formatted date
        return parseFormattedDate(formattedDate);
      }
      
      // Otherwise parse as normal formatted date
      return parseFormattedDate(dateString);
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
    }
    
    return null;
  };

  // Parse a date that's already in DD-Mon-YY or DD-Mon-YYYY format
  const parseFormattedDate = (dateString) => {
    // Convert to string only when parsing formatted dates
    const str = typeof dateString === 'string' ? dateString : String(dateString);
    if (!str || str === 'NR' || str.trim() === '') {
      return null;
    }
    
    try {
      const trimmedStr = str.trim();
      
      // Handle DD-Mon-YY or DD-Mon-YYYY format (e.g., 25-Dec-12 or 25-Dec-2012)
      if (/^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/.test(trimmedStr)) {
        const parts = trimmedStr.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthAbbr = parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
          const month = monthNames.indexOf(monthAbbr);
          
          if (month === -1) {
            console.warn('Invalid month in date string:', trimmedStr);
            return null;
          }
          
          let year = parseInt(parts[2], 10);
          if (isNaN(year)) {
            console.warn('Invalid year in date string:', trimmedStr);
            return null;
          }
          
          // Handle 2-digit years (assume 20XX if <= 50, 19XX if > 50) as per project requirements
          if (year < 100) {
            year = year <= 50 ? 2000 + year : 1900 + year;
          }
          
          if (isNaN(day) || day <= 0 || day > 31) {
            console.warn('Invalid day in date string:', trimmedStr);
            return null;
          }
          
          const date = new Date(year, month, day);
          // Validate that the date is valid
          if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
            return date;
          } else {
            console.warn('Invalid date (date overflow):', trimmedStr);
            return null;
          }
        }
      }
      
      // Try standard date parsing for other formats
      const date = new Date(trimmedStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch (error) {
      console.warn('Error parsing formatted date:', str, error);
    }
    
    return null;
  };

  const isActiveAsset = (asset) => {
    if (!asset || typeof asset !== 'object') return false;
    const normalizedStatus = String(asset.status || 'active').trim().toLowerCase();
    return normalizedStatus !== 'condemned' && normalizedStatus !== 'shifted';
  };

  // Get dashboard statistics
  const getDashboardStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    
    // Initialize counters for PM and Calibration separately
    let pmStats = {
      dueToday: 0,
      thisMonthDone: 0,
      thisMonthDue: 0,
      nextMonthDue: 0,
      completed: 0,
      futureDue: 0
    };
    
    let calStats = {
      dueToday: 0,
      thisMonthDone: 0,
      thisMonthDue: 0,
      nextMonthDue: 0,
      completed: 0,
      futureDue: 0
    };
    
    let combinedStats = {
      totalAssets: 0,
      filteredAssets: 0,
      dueToday: 0,
      thisMonthDone: 0,
      thisMonthDue: 0,
      nextMonthDue: 0,
      completed: 0,
      futureDue: 0
    };
    
    assets.forEach(asset => {
      if (!isActiveAsset(asset)) return;
      
      try {
        // Process PM dates
        const pmDueDate = parseDate(asset.pmDue);
        const pmDoneDate = parseDate(asset.pmDone);
        const normalizedPmDueDate = pmDueDate ? new Date(pmDueDate) : null;
        const normalizedPmDoneDate = pmDoneDate ? new Date(pmDoneDate) : null;
        
        // PM Due Today
        if (normalizedPmDueDate) {
          normalizedPmDueDate.setHours(0, 0, 0, 0);
          if (normalizedPmDueDate.getTime() === today.getTime()) {
            pmStats.dueToday++;
            combinedStats.dueToday++;
          }
        }
        
        // PM This Month Done
        if (normalizedPmDoneDate && normalizedPmDoneDate >= firstDayOfMonth && normalizedPmDoneDate <= lastDayOfMonth) {
          pmStats.thisMonthDone++;
          combinedStats.thisMonthDone++;
        }
        
        // PM This Month Due
        if (normalizedPmDueDate && normalizedPmDueDate >= firstDayOfMonth && normalizedPmDueDate <= lastDayOfMonth) {
          pmStats.thisMonthDue++;
          combinedStats.thisMonthDue++;
        }
        
        // PM Next Month Due
        if (normalizedPmDueDate && normalizedPmDueDate >= firstDayOfNextMonth && normalizedPmDueDate <= lastDayOfNextMonth) {
          pmStats.nextMonthDue++;
          combinedStats.nextMonthDue++;
        }
        
        // PM Completed (has done date)
        if (normalizedPmDoneDate && !isNaN(normalizedPmDoneDate.getTime())) {
          pmStats.completed++;
          combinedStats.completed++;
        }
        
        // PM Future Due (after next month only)
        if (normalizedPmDueDate && normalizedPmDueDate > lastDayOfNextMonth) {
          pmStats.futureDue++;
          combinedStats.futureDue++;
        }
        
        // Process Calibration dates
        const calDueDate = parseDate(asset.calDue);
        const calDoneDate = parseDate(asset.calDone);
        const normalizedCalDueDate = calDueDate ? new Date(calDueDate) : null;
        const normalizedCalDoneDate = calDoneDate ? new Date(calDoneDate) : null;
        
        // Calibration Due Today
        if (normalizedCalDueDate) {
          normalizedCalDueDate.setHours(0, 0, 0, 0);
          if (normalizedCalDueDate.getTime() === today.getTime()) {
            calStats.dueToday++;
            combinedStats.dueToday++;
          }
        }
        
        // Calibration This Month Done
        if (normalizedCalDoneDate && normalizedCalDoneDate >= firstDayOfMonth && normalizedCalDoneDate <= lastDayOfMonth) {
          calStats.thisMonthDone++;
          combinedStats.thisMonthDone++;
        }
        
        // Calibration This Month Due
        if (normalizedCalDueDate && normalizedCalDueDate >= firstDayOfMonth && normalizedCalDueDate <= lastDayOfMonth) {
          calStats.thisMonthDue++;
          combinedStats.thisMonthDue++;
        }
        
        // Calibration Next Month Due
        if (normalizedCalDueDate && normalizedCalDueDate >= firstDayOfNextMonth && normalizedCalDueDate <= lastDayOfNextMonth) {
          calStats.nextMonthDue++;
          combinedStats.nextMonthDue++;
        }
        
        // Calibration Completed (has done date)
        if (normalizedCalDoneDate && !isNaN(normalizedCalDoneDate.getTime())) {
          calStats.completed++;
          combinedStats.completed++;
        }
        
        // Calibration Future Due (after next month only)
        if (normalizedCalDueDate && normalizedCalDueDate > lastDayOfNextMonth) {
          calStats.futureDue++;
          combinedStats.futureDue++;
        }
      } catch (error) {
        console.warn('Error processing asset for dashboard stats:', asset, error);
      }
    });
    
    return {
      pm: pmStats,
      calibration: calStats,
      combined: combinedStats
    };
  };

  // Clear date range filter
  const clearDateRangeFilter = () => {
    setDateRangeFilter({
      startDate: '',
      endDate: '',
      type: 'all'
    });
  };

  // Toggle asset selection for bulk editing
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId) 
        : [...prev, assetId]
    );
  };

  // Select all filtered assets
  const selectAllFiltered = () => {
    if (selectedAssets.length === filteredAssets.length && filteredAssets.length > 0) {
      // Deselect all
      setSelectedAssets([]);
    } else {
      // Select all filtered assets
      setSelectedAssets(filteredAssets
        .filter(asset => asset && typeof asset === 'object' && asset.id)
        .map(asset => asset.id)
      );
    }
  };

  // Start bulk editing
  const startBulkEditing = () => {
    setBulkEditMode(true);
    setBulkEditForm({
      pmDue: '',
      pmDone: '',
      calDue: '',
      calDone: ''
    });
  };

  // Cancel bulk editing
  const cancelBulkEditing = () => {
    setBulkEditMode(false);
    setSelectedAssets([]);
    setBulkEditForm({
      pmDue: '',
      pmDone: '',
      calDue: '',
      calDone: ''
    });
  };

  // Calculate due date from done date
  const calculateDueDate = (doneDate, monthsToAdd) => {
    if (!doneDate || doneDate.trim() === '') return '';
    
    try {
      // Parse the done date
      const doneDateObj = parseDate(doneDate);
      if (!doneDateObj) return '';
      
      // Calculate due date: add months, then subtract 1 day
      const dueDate = new Date(doneDateObj);
      dueDate.setMonth(dueDate.getMonth() + monthsToAdd);
      dueDate.setDate(dueDate.getDate() - 1); // Subtract 1 day (6 months - 1 day)
      
      // Format back to DD-Mon-YYYY
      return formatToDDMonYYYY(dueDate);
    } catch (error) {
      console.warn('Error calculating due date:', error);
      return '';
    }
  };

  // Convert calendar date (YYYY-MM-DD) to DD-Mon-YY format
  const convertCalendarToCustomFormat = (calendarDate) => {
    if (!calendarDate) return '';
    
    try {
      const date = new Date(calendarDate);
      if (isNaN(date.getTime())) return '';
      
      return formatToDDMonYYYY(date);
    } catch (error) {
      console.warn('Error converting calendar date:', error);
      return '';
    }
  };

  // Convert DD-Mon-YY format to calendar date (YYYY-MM-DD)
  const convertCustomToCalendarFormat = (customDate) => {
    // Handle non-string values (Date objects, null, undefined)
    if (customDate instanceof Date) {
      const yyyy = customDate.getFullYear();
      const mm = String(customDate.getMonth() + 1).padStart(2, '0');
      const dd = String(customDate.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    if (typeof customDate !== 'string' || !customDate || customDate.trim() === '') return '';
    
    try {
      const date = parseDate(customDate);
      if (!date) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.warn('Error converting custom date to calendar format:', error);
      return '';
    }
  };

  // Handle bulk edit form changes with auto-calculation
  const handleBulkEditChange = (field, value) => {
    const updatedForm = { ...bulkEditForm, [field]: value };
    
    // Auto-set corresponding Due date when Done is set to NA or NR
    if (field === 'pmDone' && (value === 'NA' || value === 'NR')) {
      updatedForm.pmDue = value; // Set PM Due to same NA/NR
    } else if (field === 'calDone' && (value === 'NA' || value === 'NR')) {
      updatedForm.calDue = value; // Set Cal Due to same NA/NR
    }
    // Auto-calculate due dates when done dates change (and not NA/NR)
    else if (field === 'pmDone' && value && value !== 'NA' && value !== 'NR') {
      updatedForm.pmDue = calculateDueDate(value, 6); // PM Due = Done + 6 months - 1 day
    } else if (field === 'calDone' && value && value !== 'NA' && value !== 'NR') {
      updatedForm.calDue = calculateDueDate(value, 12); // Cal Due = Done + 12 months - 1 day
    }
    
    setBulkEditForm(updatedForm);
  };

  // Handle calendar date selection for bulk edit
  const handleBulkEditCalendarChange = (field, calendarDate) => {
    const customDate = convertCalendarToCustomFormat(calendarDate);
    handleBulkEditChange(field, customDate);
  };

  // Apply bulk edits
  const applyBulkEdits = () => {
    if (selectedAssets.length === 0) {
      setError('Please select at least one asset to update');
      return;
    }
    
    try {
      const updatedAssets = assets.map(asset => {
        if (asset && typeof asset === 'object' && asset.id && selectedAssets.includes(asset.id)) {
          return {
            ...asset,
            pmDue: bulkEditForm.pmDue || asset.pmDue,
            pmDone: bulkEditForm.pmDone || asset.pmDone,
            calDue: bulkEditForm.calDue || asset.calDue,
            calDone: bulkEditForm.calDone || asset.calDone
          };
        }
        return asset;
      });
      
      if (saveAssets(updatedAssets)) {
        setBulkEditMode(false);
        setSelectedAssets([]);
        setBulkEditForm({
          pmDue: '',
          pmDone: '',
          calDue: '',
          calDone: ''
        });
      }
    } catch (error) {
      console.error('Failed to apply bulk edits:', error);
      setError('Failed to apply bulk edits: ' + error.message);
    }
  };

  // Start editing an asset
  const startEditing = (asset) => {
    if (!asset || typeof asset !== 'object') return;
    
    setEditingAsset(asset);
    setEditForm({
      pmDue: formatAssetDate(asset.pmDue),
      pmDone: formatAssetDate(asset.pmDone),
      calDue: formatAssetDate(asset.calDue),
      calDone: formatAssetDate(asset.calDone)
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingAsset(null);
    setEditForm({
      pmDue: '',
      pmDone: '',
      calDue: '',
      calDone: ''
    });
  };

  // Handle edit form changes
  const handleEditChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save edited asset
  const saveEditing = () => {
    if (!editingAsset) return;
    
    try {
      const updatedAssets = assets.map(asset => {
        if (asset && typeof asset === 'object' && asset.id === editingAsset.id) {
          return {
            ...asset,
            pmDue: editForm.pmDue,
            pmDone: editForm.pmDone,
            calDue: editForm.calDue,
            calDone: editForm.calDone
          };
        }
        return asset;
      });
      
      if (saveAssets(updatedAssets)) {
        setEditingAsset(null);
        setEditForm({
          pmDue: '',
          pmDone: '',
          calDue: '',
          calDone: ''
        });
      }
    } catch (error) {
      console.error('Failed to update asset:', error);
      setError('Failed to update asset: ' + error.message);
    }
  };

  // Get dashboard statistics
  const dashboardStats = getDashboardStats();

  // Report Upload Functions
  const [isModalClosing, setIsModalClosing] = useState(false);
  
  const openReportUpload = useCallback((asset) => {
    // Use requestAnimationFrame to prevent blocking the main thread
    requestAnimationFrame(() => {
      setIsModalClosing(false);
      setSelectedAssetForReport(asset);
      // Initialize form without loading full report data to improve performance
      // The data will be loaded lazily when needed
      setReportUploadForm({
        pmReportFile: null,
        pmReportPreview: '',
        pmReportPages: asset.pmReportData ? [asset.pmReportData] : [],
        calReportFile: null,
        calReportPreview: '',
        calReportPages: asset.calReportData ? [asset.calReportData] : []
      });
      setShowReportUpload(true);
    });
  }, []);
  
  const closeReportUploadSafe = () => {
    if (isModalClosing) return;
    setIsModalClosing(true);
    setTimeout(() => {
      closeReportUpload();
    }, 100);
  };
  


  const closeReportUpload = () => {
    setShowReportUpload(false);
    setSelectedAssetForReport(null);
    setReportUploadForm({
      pmReportFile: null,
      pmReportPreview: '',
      pmReportPages: [],
      calReportFile: null,
      calReportPreview: '',
      calReportPages: []
    });
    // Stop camera if running
    stopCamera();
  };

  // Camera functions
  const startCamera = async (type) => {
    setCameraForType(type);
    setShowCamera(true);
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please ensure you have given camera permissions.');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setCameraForType(null);
    setCameraPreview(null); // Clear preview when closing
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    
    // Show preview instead of immediately saving
    setCameraPreview(imageData);
  };

  // Save the previewed photo
  const savePhoto = () => {
    if (!cameraPreview) return;
    
    // Add to pages
    if (cameraForType === 'pm') {
      setReportUploadForm(prev => ({
        ...prev,
        pmReportPages: [...prev.pmReportPages, cameraPreview],
        pmReportPreview: cameraPreview
      }));
    } else if (cameraForType === 'cal') {
      setReportUploadForm(prev => ({
        ...prev,
        calReportPages: [...prev.calReportPages, cameraPreview],
        calReportPreview: cameraPreview
      }));
    }
    
    // Close camera after saving
    stopCamera();
  };

  // Retake photo - clear preview and return to camera
  const retakePhoto = () => {
    setCameraPreview(null);
  };

  const removePage = (type, index) => {
    if (type === 'pm') {
      setReportUploadForm(prev => {
        const newPages = prev.pmReportPages.filter((_, i) => i !== index);
        return {
          ...prev,
          pmReportPages: newPages,
          pmReportPreview: newPages.length > 0 ? newPages[newPages.length - 1] : ''
        };
      });
    } else if (type === 'cal') {
      setReportUploadForm(prev => {
        const newPages = prev.calReportPages.filter((_, i) => i !== index);
        return {
          ...prev,
          calReportPages: newPages,
          calReportPreview: newPages.length > 0 ? newPages[newPages.length - 1] : ''
        };
      });
    }
  };

  // Compress image before storing
  const compressImage = (base64String, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Scale down if larger than maxWidth
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress as JPEG with specified quality
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.onerror = () => resolve(base64String); // Fallback to original
      img.src = base64String;
    });
  };

  const handleReportFileChange = async (type, file, append = false) => {
    if (!file) return;
    
    // Validate file type by extension and MIME type
    const validMimeTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const validExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!validMimeTypes.includes(file.type) && !hasValidExtension) {
      alert(`Skipping ${file.name}: Please upload only image files (JPG, PNG) or PDF files`);
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert(`Skipping ${file.name}: File size should not exceed 5MB`);
      return;
    }
    
    try {
      // Read file as base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
      
      let finalData = base64Data;
      
      // Compress images (not PDFs)
      if (file.type.startsWith('image/')) {
        finalData = await compressImage(base64Data);
      }
      
      // Update form state
      if (type === 'pm') {
        setReportUploadForm(prev => {
          if (append) {
            // Append to existing pages
            return {
              ...prev,
              pmReportFile: file,
              pmReportPreview: finalData,
              pmReportPages: [...(prev.pmReportPages || []), finalData]
            };
          } else {
            // Replace existing
            return {
              ...prev,
              pmReportFile: file,
              pmReportPreview: finalData,
              pmReportPages: [finalData]
            };
          }
        });
      } else {
        setReportUploadForm(prev => {
          if (append) {
            // Append to existing pages
            return {
              ...prev,
              calReportFile: file,
              calReportPreview: finalData,
              calReportPages: [...(prev.calReportPages || []), finalData]
            };
          } else {
            // Replace existing
            return {
              ...prev,
              calReportFile: file,
              calReportPreview: finalData,
              calReportPages: [finalData]
            };
          }
        });
      }
    } catch (error) {
      console.error('File processing error:', error);
      alert(`Failed to process ${file.name}. Please try again.`);
    }
  };

  const saveReports = () => {
    if (!selectedAssetForReport) return;
    
    try {
      const now = new Date().toISOString();
      
      const updatedAssets = assets.map(asset => {
        if (asset.id === selectedAssetForReport.id) {
          // Check if old reports exist and save them to history
          const hasOldPmReport = asset.pmReportData && asset.pmReportData !== '[STORAGE_LIMITED]';
          const hasOldCalReport = asset.calReportData && asset.calReportData !== '[STORAGE_LIMITED]';
          
          // Build PM history entry if uploading new PM report and old one exists
          let pmReportHistory = asset.pmReportHistory || [];
          if (reportUploadForm.pmReportPages.length > 0 && hasOldPmReport) {
            const pmHistoryEntry = {
              date: asset.pmReportUploadDate || now,
              fileName: asset.pmReportFileName || 'Unknown',
              reportData: asset.pmReportData,
              type: 'PM',
              replacedDate: now,
              serviceDate: asset.pmDone || null // Save the PM service date
            };
            pmReportHistory = [...pmReportHistory, pmHistoryEntry];
          }
          
          // Build Cal history entry if uploading new Cal report and old one exists
          let calReportHistory = asset.calReportHistory || [];
          if (reportUploadForm.calReportPages.length > 0 && hasOldCalReport) {
            const calHistoryEntry = {
              date: asset.calReportUploadDate || now,
              fileName: asset.calReportFileName || 'Unknown',
              reportData: asset.calReportData,
              type: 'Calibration',
              replacedDate: now,
              serviceDate: asset.calDone || null // Save the Calibration service date
            };
            calReportHistory = [...calReportHistory, calHistoryEntry];
          }
          
          return {
            ...asset,
            // PM Report - support multiple pages
            pmReportData: reportUploadForm.pmReportPages.length > 0 
              ? JSON.stringify(reportUploadForm.pmReportPages) // Store as JSON array
              : (reportUploadForm.pmReportPreview || asset.pmReportData),
            pmReportFileName: reportUploadForm.pmReportFile?.name 
              || (reportUploadForm.pmReportPages.length > 0 ? `${reportUploadForm.pmReportPages.length} pages` : null)
              || asset.pmReportFileName,
            pmReportUploadDate: (reportUploadForm.pmReportFile || reportUploadForm.pmReportPages.length > 0) 
              ? now 
              : asset.pmReportUploadDate,
            pmReportPageCount: reportUploadForm.pmReportPages.length || asset.pmReportPageCount || 1,
            pmReportHistory: pmReportHistory,
            // Calibration Report - support multiple pages
            calReportData: reportUploadForm.calReportPages.length > 0 
              ? JSON.stringify(reportUploadForm.calReportPages) // Store as JSON array
              : (reportUploadForm.calReportPreview || asset.calReportData),
            calReportFileName: reportUploadForm.calReportFile?.name 
              || (reportUploadForm.calReportPages.length > 0 ? `${reportUploadForm.calReportPages.length} pages` : null)
              || asset.calReportFileName,
            calReportUploadDate: (reportUploadForm.calReportFile || reportUploadForm.calReportPages.length > 0) 
              ? now 
              : asset.calReportUploadDate,
            calReportPageCount: reportUploadForm.calReportPages.length || asset.calReportPageCount || 1,
            calReportHistory: calReportHistory
          };
        }
        return asset;
      });
      
      if (saveAssets(updatedAssets)) {
        // Log the upload in audit log
        const uploadedTypes = [];
        if (reportUploadForm.pmReportFile || reportUploadForm.pmReportPages.length > 0) {
          uploadedTypes.push(`PM (${reportUploadForm.pmReportPages.length || 1} pages)`);
        }
        if (reportUploadForm.calReportFile || reportUploadForm.calReportPages.length > 0) {
          uploadedTypes.push(`Calibration (${reportUploadForm.calReportPages.length || 1} pages)`);
        }
        
        if (uploadedTypes.length > 0) {
          logUpload(
            'PM_CAL',
            selectedAssetForReport.assetId || selectedAssetForReport.id,
            `Uploaded ${uploadedTypes.join(' and ')} for ${selectedAssetForReport.equipmentName || 'Unknown Equipment'} (Asset: ${selectedAssetForReport.assetId || 'N/A'})`,
            currentUser
          );
        }
        
        // Close modal first, then show success message
        closeReportUpload();
        
        // Use a small timeout to ensure modal closes before alert
        setTimeout(() => {
          alert('Reports uploaded successfully!');
        }, 100);
      }
    } catch (error) {
      console.error('Failed to save reports:', error);
      alert('Failed to save reports: ' + error.message);
    }
  };

  const viewReport = (asset, type) => {
    const reportData = type === 'pm' ? asset.pmReportData : asset.calReportData;
    if (reportData) {
      window.open(reportData, '_blank');
    } else {
      alert('No report available');
    }
  };

  // View Documents Modal Functions
  const openViewDocuments = (asset) => {
    if (!asset) {
      console.error('Cannot open View Documents: asset is null or undefined');
      return;
    }
    // Set all states at once to avoid multiple re-renders
    setSelectedAssetForView(asset);
    setCurrentPage(0);
    setCurrentReportType('pm');
    setImageLoading(false); // Don't show loading immediately
    setImageError(false);
    // Initialize editing dates from asset (convert to YYYY-MM-DD format for date inputs)
    setEditingDates({
      pmDone: convertCustomToCalendarFormat(asset.pmDone) || (asset.pmDone === 'NA' || asset.pmDone === 'NR' ? asset.pmDone : ''),
      pmDue: convertCustomToCalendarFormat(asset.pmDue) || (asset.pmDue === 'NA' || asset.pmDue === 'NR' ? asset.pmDue : ''),
      calDone: convertCustomToCalendarFormat(asset.calDone) || (asset.calDone === 'NA' || asset.calDone === 'NR' ? asset.calDone : ''),
      calDue: convertCustomToCalendarFormat(asset.calDue) || (asset.calDue === 'NA' || asset.calDue === 'NR' ? asset.calDue : '')
    });
    setDateSaveMessage('');
    setShowViewDocuments(true);
  };

  const closeViewDocuments = () => {
    setShowViewDocuments(false);
    setSelectedAssetForView(null);
    setCurrentPage(0);
    setImageLoading(true);
    setImageError(false);
    setEditingDates({
      pmDone: '',
      pmDue: '',
      calDone: '',
      calDue: ''
    });
    setDateSaveMessage('');
  };

  // Sanitize image data to remove C2PA metadata and fix malformed data URLs
  const sanitizeImageData = (data) => {
    if (!data || typeof data !== 'string') return data;
    
    // Handle storage limited marker
    if (data === '[STORAGE_LIMITED]') return data;
    
    // Remove HTML entities that may have been encoded
    let cleaned = data
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#39;/g, "'");
    
    // Remove array brackets if the data was incorrectly wrapped
    cleaned = cleaned.replace(/^\[/, '').replace(/\]$/, '');
    
    // Remove C2PA metadata - find the actual data URL start
    const dataUrlMatch = cleaned.match(/(data:image\/[^;]+;base64,[A-Za-z0-9+/=]+)/);
    if (dataUrlMatch) {
      return dataUrlMatch[1];
    }
    
    // Check for PDF data URL
    const pdfMatch = cleaned.match(/(data:application\/pdf;base64,[A-Za-z0-9+/=]+)/);
    if (pdfMatch) {
      return pdfMatch[1];
    }
    
    // If no match found but data starts with data:, return as-is
    if (cleaned.startsWith('data:')) {
      return cleaned;
    }
    
    // Return original if we can't clean it
    return data;
  };

  const getReportPages = (asset, type) => {
    if (!asset) return [];
    const reportData = type === 'pm' ? asset.pmReportData : asset.calReportData;
    if (!reportData) return [];
    
    // Check if data was stripped due to storage limits
    if (reportData === '[STORAGE_LIMITED]') {
      return ['[STORAGE_LIMITED]'];
    }
    
    // Check if it's a JSON array (multi-page) or single image
    try {
      const parsed = JSON.parse(reportData);
      if (Array.isArray(parsed)) {
        // Filter out any storage limited entries and sanitize each page
        return parsed
          .filter(page => page !== '[STORAGE_LIMITED]')
          .map(page => sanitizeImageData(page));
      }
    } catch (e) {
      // Not JSON, treat as single image - sanitize it
      return [sanitizeImageData(reportData)];
    }
    return [sanitizeImageData(reportData)];
  };

  const getCurrentReportPageData = (asset, type, pageIndex = 0) => {
    const pages = getReportPages(asset, type);
    if (!pages || pages.length === 0) return null;
    return pages[pageIndex] || pages[0] || null;
  };

  const getHistoryReportPages = (historyEntry) => {
    if (!historyEntry) return [];

    const rawData = historyEntry.reportData || historyEntry.data;
    if (!rawData) return [];
    if (rawData === '[STORAGE_LIMITED]') return ['[STORAGE_LIMITED]'];

    try {
      const parsed = JSON.parse(rawData);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((page) => page && page !== '[STORAGE_LIMITED]')
          .map((page) => sanitizeImageData(page));
      }
    } catch (_error) {
      return [sanitizeImageData(rawData)];
    }

    return [sanitizeImageData(rawData)];
  };

  const hasReport = (asset, type) => {
    if (!asset) return false;
    const reportData = type === 'pm' ? asset.pmReportData : asset.calReportData;
    return !!reportData;
  };

  const deleteReport = (asset, type) => {
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'manager') {
      alert('Access denied. Only Admin or Manager can delete reports.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the ${type.toUpperCase()} report?`)) {
      return;
    }
    
    try {
      const updatedAssets = assets.map(a => {
        if (a.id === asset.id || a.assetId === asset.assetId) {
          if (type === 'pm') {
            return {
              ...a,
              pmReportData: null,
              pmReportFileName: null,
              pmReportUploadDate: null
            };
          } else {
            return {
              ...a,
              calReportData: null,
              calReportFileName: null,
              calReportUploadDate: null
            };
          }
        }
        return a;
      });
      
      if (saveAssets(updatedAssets)) {
        // Log the deletion in audit log
        logDeletion(
          'PM_CAL',
          asset.assetId || asset.id,
          `Deleted ${type.toUpperCase()} report for ${asset.equipmentName || 'Unknown Equipment'} (Asset: ${asset.assetId || 'N/A'})`,
          currentUser
        );
        
        alert(`${type.toUpperCase()} report deleted successfully!`);
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
      alert('Failed to delete report: ' + error.message);
    }
  };

  // Handle Replace Report
  const handleReplaceReport = async (asset, type, file) => {
    try {
      console.log(`Replacing ${type} report for asset:`, asset.assetId);
      
      // Read file as base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const reportData = e.target.result;
        const now = new Date().toISOString();
        
        const updatedAssets = assets.map(a => {
          if (a.id === asset.id || a.assetId === asset.assetId) {
            // Check if old report exists
            const hasOldPmReport = a.pmReportData && a.pmReportData !== '[STORAGE_LIMITED]';
            const hasOldCalReport = a.calReportData && a.calReportData !== '[STORAGE_LIMITED]';
            
            console.log(`History debug - hasOldPmReport: ${hasOldPmReport}, hasOldCalReport: ${hasOldCalReport}`);
            console.log(`History debug - Current PM history count: ${a.pmReportHistory?.length || 0}`);
            console.log(`History debug - Current Cal history count: ${a.calReportHistory?.length || 0}`);
            
            // Build history entry for old report if it exists
            const oldHistoryEntry = type === 'pm' 
              ? (hasOldPmReport ? {
                  date: a.pmReportUploadDate || now,
                  fileName: a.pmReportFileName || 'Unknown',
                  reportData: a.pmReportData,
                  type: 'PM',
                  replacedDate: now,
                  serviceDate: a.pmDone || null // Save the PM service date
                } : null)
              : (hasOldCalReport ? {
                  date: a.calReportUploadDate || now,
                  fileName: a.calReportFileName || 'Unknown',
                  reportData: a.calReportData,
                  type: 'Calibration',
                  replacedDate: now,
                  serviceDate: a.calDone || null // Save the Calibration service date
                } : null);
            
            console.log(`History debug - oldHistoryEntry created:`, oldHistoryEntry ? 'YES' : 'NO');
            
            // Add old report to history
            const currentHistory = type === 'pm' 
              ? (a.pmReportHistory || [])
              : (a.calReportHistory || []);
            
            const newHistory = oldHistoryEntry 
              ? [...currentHistory, oldHistoryEntry]
              : currentHistory;
            
            console.log(`History debug - newHistory length: ${newHistory.length}`);
            
            if (type === 'pm') {
              return {
                ...a,
                pmReportData: reportData,
                pmReportFileName: file.name,
                pmReportUploadDate: now,
                pmReportHistory: newHistory
              };
            } else {
              return {
                ...a,
                calReportData: reportData,
                calReportFileName: file.name,
                calReportUploadDate: now,
                calReportHistory: newHistory
              };
            }
          }
          return a;
        });
        
        if (saveAssets(updatedAssets)) {
          // Log the replacement in audit log
          logUpload(
            'PM_CAL',
            asset.assetId || asset.id,
            `Replaced ${type.toUpperCase()} report for ${asset.equipmentName || 'Unknown Equipment'} (Asset: ${asset.assetId || 'N/A'}) - New file: ${file.name}`,
            currentUser
          );
          
          alert(`${type.toUpperCase()} report replaced successfully! Previous report saved to history.`);
          
          // Update the selected asset for view to reflect changes
          const updatedAsset = updatedAssets.find(a => a.id === asset.id || a.assetId === asset.assetId);
          if (updatedAsset) {
            setSelectedAssetForView(updatedAsset);
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to replace report:', error);
      alert('Failed to replace report: ' + error.message);
    }
  };

  // Bulk Report Upload Functions
  const openBulkReportUpload = useCallback(() => {
    console.log('Opening bulk upload modal');
    setShowBulkReportUpload(true);
    setBulkReportFiles([]);
    setBulkUploadProgress({ current: 0, total: 0, matched: 0, unmatched: [] });
  }, []);

  const closeBulkReportUpload = () => {
    console.log('Closing bulk upload modal');
    setShowBulkReportUpload(false);
    setBulkReportFiles([]);
    setBulkUploadProgress({ current: 0, total: 0, matched: 0, unmatched: [] });
    setIsBulkUploading(false);
    setBulkUploadSuccess(null);
  };

  const handleBulkFileSelect = (files) => {
    if (!files || files.length === 0) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const validFiles = Array.from(files).filter(file => {
      if (!validTypes.includes(file.type)) {
        console.warn(`Skipping ${file.name}: Invalid file type`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        console.warn(`Skipping ${file.name}: File too large (max 5MB)`);
        return false;
      }
      return true;
    });
    
    setBulkReportFiles(prev => [...prev, ...validFiles]);
    // Clear previous success message when new files are selected
    setBulkUploadSuccess(null);
  };

  const removeBulkFile = (index) => {
    setBulkReportFiles(prev => prev.filter((_, i) => i !== index));
    // Clear success message when files are modified
    setBulkUploadSuccess(null);
  };

  const processBulkUpload = async () => {
    if (bulkReportFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    setIsBulkUploading(true);
    setBulkUploadProgress({ current: 0, total: bulkReportFiles.length, matched: 0, unmatched: [] });

    const unmatched = [];
    let matched = 0;
    const updatedAssets = [...assets];

    for (let i = 0; i < bulkReportFiles.length; i++) {
      const file = bulkReportFiles[i];
      setBulkUploadProgress(prev => ({ ...prev, current: i + 1 }));

      // Try to match file name with asset
      // Expected formats: "AssetID_PM.pdf", "AssetID_CAL.jpg", "SerialNo_PM.png", etc.
      const fileName = file.name.toLowerCase();
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      
      // Parse filename: look for PM or CAL indicator
      const isPM = fileName.includes('_pm') || fileName.includes('-pm') || fileName.includes(' pm') || fileName.includes('preventive');
      const isCal = fileName.includes('_cal') || fileName.includes('-cal') || fileName.includes(' cal') || fileName.includes('calibration') || fileName.includes('_c_');
      
      // Extract identifier (Asset ID or Serial Number)
      let identifier = nameWithoutExt
        .replace(/_pm$/i, '').replace(/-pm$/i, '').replace(/ pm$/i, '')
        .replace(/_cal$/i, '').replace(/-cal$/i, '').replace(/ cal$/i, '')
        .replace(/_c_$/i, '').replace(/-c-$/i, '')
        .replace(/_preventive$/i, '').replace(/-preventive$/i, '')
        .replace(/_calibration$/i, '').replace(/-calibration$/i, '')
        .trim();

      // Find matching asset
      const matchingAsset = updatedAssets.find(asset => {
        const assetId = (asset.assetId || '').toString().toLowerCase().trim();
        const serialNo = (asset.serialNo || '').toString().toLowerCase().trim();
        const searchId = identifier.toLowerCase().trim();
        
        return assetId === searchId || 
               serialNo === searchId ||
               assetId.includes(searchId) || 
               serialNo.includes(searchId) ||
               searchId.includes(assetId) ||
               searchId.includes(serialNo);
      });

      if (matchingAsset && (isPM || isCal)) {
        try {
          const base64Data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(file);
          });

          const assetIndex = updatedAssets.findIndex(a => a.id === matchingAsset.id);
          if (assetIndex !== -1) {
            if (isPM) {
              updatedAssets[assetIndex] = {
                ...updatedAssets[assetIndex],
                pmReportData: base64Data,
                pmReportFileName: file.name,
                pmReportUploadDate: new Date().toISOString()
              };
            } else if (isCal) {
              updatedAssets[assetIndex] = {
                ...updatedAssets[assetIndex],
                calReportData: base64Data,
                calReportFileName: file.name,
                calReportUploadDate: new Date().toISOString()
              };
            }
            matched++;
            setBulkUploadProgress(prev => ({ ...prev, matched }));
          }
        } catch (error) {
          console.error(`Error processing ${file.name}:`, error);
          unmatched.push({ file: file.name, reason: 'Processing error' });
        }
      } else {
        unmatched.push({ 
          file: file.name, 
          reason: matchingAsset ? 'Could not determine PM or CAL' : 'No matching asset found'
        });
      }
    }

    // Save all updates
    if (saveAssets(updatedAssets)) {
      setBulkUploadProgress(prev => ({ ...prev, unmatched }));
      
      // Log the bulk upload in audit log
      if (matched > 0) {
        logUpload(
          'PM_CAL',
          'BULK',
          `Bulk uploaded ${matched} PM/Calibration reports (${unmatched.length} unmatched)`,
          currentUser
        );
      }
      
      // Set success message instead of alert
      setBulkUploadSuccess({ matched, unmatched: unmatched.length });
    } else {
      // Show error if save failed
      setBulkUploadSuccess({ error: true, message: 'Failed to save uploaded reports. Please try again.' });
    }

    setIsBulkUploading(false);
  };

  // Smart Multi-Equipment Assignment Functions
  const [isOpeningSmartAssign, setIsOpeningSmartAssign] = useState(false);
  
  // Memoized open function to prevent re-renders
  const openSmartAssign = useCallback(() => {
    console.log('Opening smart assign modal');
    setIsOpeningSmartAssign(true);
    
    // Use requestAnimationFrame for smoother UI update
    requestAnimationFrame(() => {
      setShowSmartAssign(true);
      setSmartAssignForm({
        equipmentType: '',
        reportType: 'pm',
        reportFile: null,
        reportPreview: '',
        reportPages: [],
        matchedAssets: [],
        assignToAll: false
      });
      setIsOpeningSmartAssign(false);
    });
  }, []);

  const closeSmartAssign = useCallback(() => {
    console.log('Closing smart assign modal');
    setShowSmartAssign(false);
    // Stop camera if running - use the ref for latest value
    const currentStream = streamRef.current;
    if (currentStream) {
      currentStream.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setStream(null);
    }
  }, []);
  
  // Bulk Upload Modal Functions
  const openBulkUpload = useCallback(() => {
    console.log('Opening bulk upload modal');
    setIsOpeningBulkUpload(true);
    
    // Use requestAnimationFrame for smoother UI update
    requestAnimationFrame(() => {
      setShowBulkUpload(true);
      setIsOpeningBulkUpload(false);
    });
  }, []);
  
  const closeBulkUpload = () => {
    console.log('Closing bulk upload modal');
    setShowBulkUpload(false);
  };
  
  const handleBulkUploadComplete = useCallback((uploadData) => {
    const { assetId, reportData, reportType, fileName, pageCount, source } = uploadData;
    
    // Use functional update to get latest assets state
    setAssets(currentAssets => {
      const normalizedTargetId = String(assetId ?? '').trim();

      // Find the asset
      const asset = currentAssets.find(a => {
        const idMatch = String(a.id ?? '').trim() === normalizedTargetId;
        const assetIdMatch = String(a.assetId ?? '').trim() === normalizedTargetId;
        return idMatch || assetIdMatch;
      });
      if (!asset) {
        console.error('Asset not found for bulk upload:', assetId);
        return currentAssets;
      }
      
      // Update asset with report data
      const now = new Date().toISOString();
      const updatedAssets = currentAssets.map(a => {
        const sameAsset = String(a.id ?? '').trim() === String(asset.id ?? '').trim() ||
          String(a.assetId ?? '').trim() === String(asset.assetId ?? '').trim();

        if (sameAsset) {
          // Create history entry
          const historyEntry = {
            date: now,
            fileName: fileName,
            pageCount: pageCount,
            source: source || 'bulk_upload'
          };
          
          if (reportType === 'pm') {
            const newHistory = a.pmReportHistory ? [...a.pmReportHistory, historyEntry] : [historyEntry];
            return {
              ...a,
              pmReportData: reportData,
              pmReportFileName: fileName,
              pmReportUploadDate: now,
              pmReportHistory: newHistory,
              pmReportPageCount: pageCount
            };
          } else if (reportType === 'both') {
            const pmHistory = a.pmReportHistory ? [...a.pmReportHistory, historyEntry] : [historyEntry];
            const calHistory = a.calReportHistory ? [...a.calReportHistory, historyEntry] : [historyEntry];
            return {
              ...a,
              pmReportData: reportData,
              pmReportFileName: fileName,
              pmReportUploadDate: now,
              pmReportHistory: pmHistory,
              pmReportPageCount: pageCount,
              calReportData: reportData,
              calReportFileName: fileName,
              calReportUploadDate: now,
              calReportHistory: calHistory,
              calReportPageCount: pageCount
            };
          } else {
            const newHistory = a.calReportHistory ? [...a.calReportHistory, historyEntry] : [historyEntry];
            return {
              ...a,
              calReportData: reportData,
              calReportFileName: fileName,
              calReportUploadDate: now,
              calReportHistory: newHistory,
              calReportPageCount: pageCount
            };
          }
        }
        return a;
      });
      
      if (!saveAssets(updatedAssets)) {
        alert('Failed to save report data on server.');
        return currentAssets;
      }

      // Log the upload
      logUpload(
        'PM_CAL',
        asset.assetId || asset.id,
        `Bulk ${reportType.toUpperCase()} report uploaded for ${asset.equipmentName || 'Unknown Equipment'} via ${source}`,
        currentUser
      );

      console.log(`Bulk upload completed for asset ${assetId}`);
      
      return updatedAssets;
    });
  }, [currentUser]);

  const handleSmartAssignFileChange = (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload only image files (JPG, PNG) or PDF files');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should not exceed 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setSmartAssignForm(prev => ({
        ...prev,
        reportFile: file,
        reportPreview: e.target.result,
        reportPages: file.type.startsWith('image/') ? [e.target.result] : prev.reportPages
      }));
    };
    reader.readAsDataURL(file);
  };

  const startSmartAssignCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      setStream(mediaStream);
      
      // Create a temporary video element for capture
      const video = document.createElement('video');
      video.srcObject = mediaStream;
      video.autoplay = true;
      
      // Store video element for capture
      window.smartAssignCamera = video;
      
      alert('Camera started! Click "Capture Photo" to take a picture.');
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please ensure you have given camera permissions.');
    }
  };

  const captureSmartAssignPhoto = () => {
    if (!window.smartAssignCamera) {
      alert('Please start camera first');
      return;
    }
    
    const video = window.smartAssignCamera;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    
    setSmartAssignForm(prev => ({
      ...prev,
      reportPages: [...prev.reportPages, imageData],
      reportPreview: imageData
    }));
  };

  const removeSmartAssignPage = (index) => {
    setSmartAssignForm(prev => ({
      ...prev,
      reportPages: prev.reportPages.filter((_, i) => i !== index)
    }));
  };

  const searchMatchingAssets = () => {
    const searchTerm = smartAssignForm.equipmentType.toLowerCase().trim();
    if (!searchTerm) {
      alert('Please enter equipment type/name to search');
      return;
    }

    const matched = assets.filter(asset => {
      const equipmentName = String(asset.equipmentName || '').toLowerCase();
      const assetId = String(asset.assetId || '').toLowerCase();
      const serialNo = String(asset.serialNo || '').toLowerCase();
      const department = String(asset.department || '').toLowerCase();
      
      return equipmentName.includes(searchTerm) ||
             assetId.includes(searchTerm) ||
             serialNo.includes(searchTerm) ||
             department.includes(searchTerm);
    });

    setSmartAssignForm(prev => ({
      ...prev,
      matchedAssets: matched,
      assignToAll: false
    }));

    if (matched.length === 0) {
      alert('No equipment found matching your search term');
    }
  };

  const executeSmartAssign = async () => {
    if (smartAssignForm.matchedAssets.length === 0) {
      alert('Please search for equipment first');
      return;
    }

    if (!smartAssignForm.reportFile && smartAssignForm.reportPages.length === 0) {
      alert('Please upload or capture a report');
      return;
    }

    const targetAssets = smartAssignForm.assignToAll 
      ? smartAssignForm.matchedAssets 
      : smartAssignForm.matchedAssets.filter((_, idx) => 
          document.getElementById(`asset-check-${idx}`)?.checked
        );

    if (targetAssets.length === 0) {
      alert('Please select at least one equipment to assign the report');
      return;
    }

    if (!window.confirm(`Assign this report to ${targetAssets.length} equipment(s)?`)) {
      return;
    }

    try {
      const updatedAssets = [...assets];
      const reportData = smartAssignForm.reportPages.length > 0 
        ? JSON.stringify(smartAssignForm.reportPages)
        : smartAssignForm.reportPreview;
      const pageCount = smartAssignForm.reportPages.length || 1;

      for (const targetAsset of targetAssets) {
        const assetIndex = updatedAssets.findIndex(a => a.id === targetAsset.id);
        if (assetIndex !== -1) {
          if (smartAssignForm.reportType === 'pm') {
            updatedAssets[assetIndex] = {
              ...updatedAssets[assetIndex],
              pmReportData: reportData,
              pmReportFileName: smartAssignForm.reportFile?.name || `${pageCount} pages (Multi-equipment)`,
              pmReportUploadDate: new Date().toISOString(),
              pmReportPageCount: pageCount
            };
          } else {
            updatedAssets[assetIndex] = {
              ...updatedAssets[assetIndex],
              calReportData: reportData,
              calReportFileName: smartAssignForm.reportFile?.name || `${pageCount} pages (Multi-equipment)`,
              calReportUploadDate: new Date().toISOString(),
              calReportPageCount: pageCount
            };
          }
        }
      }

      if (saveAssets(updatedAssets)) {
        // Log in audit log
        logUpload(
          'PM_CAL',
          'MULTI',
          `Assigned ${smartAssignForm.reportType.toUpperCase()} report to ${targetAssets.length} equipment(s) matching "${smartAssignForm.equipmentType}"`,
          currentUser
        );

        alert(`Successfully assigned report to ${targetAssets.length} equipment(s)!`);
        closeSmartAssign();
      }
    } catch (error) {
      console.error('Error in smart assign:', error);
      alert('Failed to assign reports: ' + error.message);
    }
  };

  // Handler for SmartAssignModal execution
  const handleSmartAssignExecute = useCallback(async ({ targetAssets, reportData, reportType, pageCount, pmReportData, pmFileName, calReportData, calFileName }) => {
    try {
      // Use functional update to get latest assets state
      setAssets(currentAssets => {
        const updatedAssets = [...currentAssets];
        const now = new Date().toISOString();
        
        // Handle both PM and Calibration together
        if (reportType === 'both') {
          for (const targetAsset of targetAssets) {
            const assetIndex = updatedAssets.findIndex(a => a.id === targetAsset.id);
            if (assetIndex !== -1) {
              updatedAssets[assetIndex] = {
                ...updatedAssets[assetIndex],
                pmReportData: pmReportData,
                pmReportFileName: pmFileName,
                pmReportUploadDate: now,
                calReportData: calReportData,
                calReportFileName: calFileName,
                calReportUploadDate: now
              };
            }
          }
        } else {
          // Single report type (PM or Cal)
          const fileName = `${pageCount} pages (Multi-equipment)`;
          
          for (const targetAsset of targetAssets) {
            const assetIndex = updatedAssets.findIndex(a => a.id === targetAsset.id);
            if (assetIndex !== -1) {
              if (reportType === 'pm') {
                updatedAssets[assetIndex] = {
                  ...updatedAssets[assetIndex],
                  pmReportData: reportData,
                  pmReportFileName: fileName,
                  pmReportUploadDate: now,
                  pmReportPageCount: pageCount
                };
              } else {
                updatedAssets[assetIndex] = {
                  ...updatedAssets[assetIndex],
                  calReportData: reportData,
                  calReportFileName: fileName,
                  calReportUploadDate: now,
                  calReportPageCount: pageCount
                };
              }
            }
          }
        }
        
        if (!saveAssets(updatedAssets)) {
          alert('Failed to save reports on server.');
          return currentAssets;
        }
        
        return updatedAssets;
      });
      
      // Log and notify outside of setAssets
      if (reportType === 'both') {
        logUpload(
          'PM_CAL',
          'MULTI',
          `Assigned BOTH PM and Calibration reports to ${targetAssets.length} equipment(s)`,
          currentUser
        );
        alert(`Successfully assigned PM and Calibration reports to ${targetAssets.length} equipment(s)!`);
      } else {
        logUpload(
          'PM_CAL',
          'MULTI',
          `Assigned ${reportType.toUpperCase()} report to ${targetAssets.length} equipment(s)`,
          currentUser
        );
        alert(`Successfully assigned report to ${targetAssets.length} equipment(s)!`);
      }
      closeSmartAssign();
    } catch (error) {
      console.error('Error in smart assign:', error);
      alert('Failed to assign reports: ' + error.message);
    }
  }, [currentUser, closeSmartAssign]);

  if (loading) {
    return (
      <div className="pm-calibration-tracker-container">
        <div className="card">
          <h4 className="card-title" style={{fontSize: '1rem', marginBottom: '0.5rem', paddingBottom: '5px'}}>🔧 PM & Calibration Tracker</h4>
          <p>Loading assets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-calibration-tracker-container">
        <div className="card">
          <h4 className="card-title" style={{fontSize: '1rem', marginBottom: '0.5rem', paddingBottom: '5px'}}>🔧 PM & Calibration Tracker</h4>
          <div className="error-message">❌ {error}</div>
          <button onClick={loadAssets} className="btn btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  // Calculate active assets (excluding condemned and shifted)
  const activeAssetsCount = assets.filter(asset => {
    return isActiveAsset(asset);
  }).length;

  return (
    <div className="pm-calibration-tracker-container">
      <div className="card">
        <h4 className="card-title" style={{fontSize: '1rem', marginBottom: '0.5rem', paddingBottom: '5px'}}>🔧 PM & Calibration Tracker</h4>
        
        {currentUser && (
          <div className="user-badge">
            👤 Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        
        {/* Dashboard Summary */}
        <div className="dashboard-summary">
          <h5 style={{fontSize: '0.75rem', margin: '0 0 0.25rem 0'}}>Dashboard Summary</h5>
          
          {/* Combined Stats */}
          <div className="summary-grid">
            <div className="summary-card total-assets">
              <div className="summary-value">{activeAssetsCount}</div>
              <div className="summary-label">Total Assets</div>
            </div>
            <div className="summary-card filtered-assets">
              <div className="summary-value">{filteredAssets.length}</div>
              <div className="summary-label">Filtered Assets</div>
            </div>
            <div className="summary-card due-today">
              <div className="summary-value">{dashboardStats.combined.dueToday}</div>
              <div className="summary-label">Due Today</div>
            </div>
            <div className="summary-card this-month-done">
              <div className="summary-value">{dashboardStats.combined.thisMonthDone}</div>
              <div className="summary-label">This Month Done</div>
            </div>
            <div className="summary-card this-month-due">
              <div className="summary-value">{dashboardStats.combined.thisMonthDue}</div>
              <div className="summary-label">This Month Due</div>
            </div>
            <div className="summary-card next-month-due">
              <div className="summary-value">{dashboardStats.combined.nextMonthDue}</div>
              <div className="summary-label">Next Month Due</div>
            </div>
            <div className="summary-card future-due">
              <div className="summary-value">{dashboardStats.combined.futureDue}</div>
              <div className="summary-label">Future Due</div>
            </div>
            <div className="summary-card completed">
              <div className="summary-value">{dashboardStats.combined.completed}</div>
              <div className="summary-label">Completed</div>
            </div>
          </div>
          
          {/* PM Stats */}
          <h4>PM Statistics</h4>
          <div className="summary-grid">
            <div className="summary-card pm-due-today">
              <div className="summary-value">{dashboardStats.pm.dueToday}</div>
              <div className="summary-label">PM Due Today</div>
            </div>
            <div className="summary-card pm-this-month-done">
              <div className="summary-value">{dashboardStats.pm.thisMonthDone}</div>
              <div className="summary-label">PM This Month Done</div>
            </div>
            <div className="summary-card pm-this-month-due">
              <div className="summary-value">{dashboardStats.pm.thisMonthDue}</div>
              <div className="summary-label">PM This Month Due</div>
            </div>
            <div className="summary-card pm-next-month-due">
              <div className="summary-value">{dashboardStats.pm.nextMonthDue}</div>
              <div className="summary-label">PM Next Month Due</div>
            </div>
            <div className="summary-card pm-future-due">
              <div className="summary-value">{dashboardStats.pm.futureDue}</div>
              <div className="summary-label">PM Future Due</div>
            </div>
            <div className="summary-card pm-completed">
              <div className="summary-value">{dashboardStats.pm.completed}</div>
              <div className="summary-label">PM Completed</div>
            </div>
          </div>
          
          {/* Calibration Stats */}
          <h4>Calibration Statistics</h4>
          <div className="summary-grid">
            <div className="summary-card cal-due-today">
              <div className="summary-value">{dashboardStats.calibration.dueToday}</div>
              <div className="summary-label">Cal Due Today</div>
            </div>
            <div className="summary-card cal-this-month-done">
              <div className="summary-value">{dashboardStats.calibration.thisMonthDone}</div>
              <div className="summary-label">Cal This Month Done</div>
            </div>
            <div className="summary-card cal-this-month-due">
              <div className="summary-value">{dashboardStats.calibration.thisMonthDue}</div>
              <div className="summary-label">Cal This Month Due</div>
            </div>
            <div className="summary-card cal-next-month-due">
              <div className="summary-value">{dashboardStats.calibration.nextMonthDue}</div>
              <div className="summary-label">Cal Next Month Due</div>
            </div>
            <div className="summary-card cal-future-due">
              <div className="summary-value">{dashboardStats.calibration.futureDue}</div>
              <div className="summary-label">Cal Future Due</div>
            </div>
            <div className="summary-card cal-completed">
              <div className="summary-value">{dashboardStats.calibration.completed}</div>
              <div className="summary-label">Cal Completed</div>
            </div>
          </div>
          
        </div>
        
        {/* Filter Section */}
        <div className="filter-section">
          <h3>🔍 Search & Filters</h3>
          
          {/* Primary Search Row */}
          <div className="filter-row primary-search">
            <div className="filter-group search-large">
              <label htmlFor="equipmentSearch">🔧 Equipment Name:</label>
              <input
                type="text"
                id="equipmentSearch"
                className="search-input-large"
                placeholder="Search by equipment name... (e.g., X-Ray, ECG, Ventilator)"
                value={equipmentNameFilter}
                onChange={(e) => setEquipmentNameFilter(e.target.value)}
              />
              {equipmentNameFilter && (
                <button 
                  className="btn-clear-inline"
                  onClick={() => setEquipmentNameFilter('')}
                  title="Clear equipment search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {/* Secondary Search Row */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="search">🔎 General Search:</label>
              <input
                type="text"
                id="search"
                placeholder="Search all fields (Asset ID, Serial No, Department, etc.)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="btn-clear-inline"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            
            <div className="filter-group">
              <label htmlFor="maintenanceType">🔧 Maintenance Type:</label>
              <select 
                id="maintenanceType"
                value={maintenanceTypeFilter}
                onChange={(e) => setMaintenanceTypeFilter(e.target.value)}
              >
                <option value="all">All (PM & Cal)</option>
                <option value="pm">PM Only</option>
                <option value="calibration">Calibration Only</option>
                <option value="both">PM + CAL (Both)</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="statusFilter">📊 Status:</label>
              <select 
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="due">Due Only</option>
                <option value="done">Done Only</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="dateFilter">📅 Quick Filter:</label>
              <select 
                id="dateFilter"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                disabled={dateRangeFilter.startDate || dateRangeFilter.endDate}
              >
                <option value="all">All Dates</option>
                <option value="dueToday">Due Today</option>
                <option value="thisMonthDone">This Month Done</option>
                <option value="thisMonthDue">This Month Due</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>&nbsp;</label>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setEquipmentNameFilter('');
                  setMaintenanceTypeFilter('all');
                  setStatusFilter('all');
                  setDateFilter('all');
                  clearDateRangeFilter();
                  setColumnFilters({
                    department: '',
                    equipmentName: '',
                    assetId: '',
                    serialNo: '',
                    pmDue: { start: '', end: '' },
                    pmDone: { start: '', end: '' },
                    calDue: { start: '', end: '' },
                    calDone: { start: '', end: '' }
                  });
                }}
                title="Clear all filters including column filters"
              >
                🔄 Clear All Filters
              </button>
            </div>
            
          </div>
          
          {/* Date Range Filter */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="startDate">Start Date:</label>
              <input
                type="date"
                id="startDate"
                value={dateRangeFilter.startDate}
                onChange={(e) => setDateRangeFilter(prev => ({...prev, startDate: e.target.value}))}
                disabled={dateFilter !== 'all'}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="endDate">End Date:</label>
              <input
                type="date"
                id="endDate"
                value={dateRangeFilter.endDate}
                onChange={(e) => setDateRangeFilter(prev => ({...prev, endDate: e.target.value}))}
                disabled={dateFilter !== 'all'}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="dateRangeType">Filter Type:</label>
              <select
                id="dateRangeType"
                value={dateRangeFilter.type}
                onChange={(e) => setDateRangeFilter(prev => ({...prev, type: e.target.value}))}
                disabled={dateFilter !== 'all'}
              >
                <option value="all">All Dates</option>
                <option value="due">Due Dates Only</option>
                <option value="done">Done Dates Only</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Action Buttons Section - Separate container above Bulk Editing */}
        <div className="action-buttons-section">
          <div className="action-buttons-container">
            <StableSmartAssignButton 
              onClick={openSmartAssign} 
              isLoading={isOpeningSmartAssign}
              visible={!loading && !showReportUpload && !showBulkReportUpload && !showSmartAssign && !showCamera && !showBulkUpload}
            />
            <StableBulkUploadButton 
              onClick={openBulkUpload} 
              isLoading={isOpeningBulkUpload}
              visible={!loading && !showReportUpload && !showBulkReportUpload && !showSmartAssign && !showCamera && !showBulkUpload}
            />
          </div>
        </div>
        
        {/* Bulk Edit Section */}
        <div className="bulk-edit-section">
          <div className="bulk-edit-header">
            <h3>Bulk Editing</h3>
            <div className="bulk-edit-controls">
              {!bulkEditMode ? (
                <button 
                  className="btn btn-secondary"
                  onClick={startBulkEditing}
                >
                  Bulk Edit
                </button>
              ) : (
                <>
                  <button 
                    className="btn btn-secondary"
                    onClick={selectAllFiltered}
                  >
                    {selectedAssets.length === filteredAssets.length && filteredAssets.length > 0 
                      ? 'Deselect All' 
                      : 'Select All'}
                  </button>
                  <span>{selectedAssets.length} assets selected</span>
                </>
              )}
            </div>
          </div>
          
          {bulkEditMode && (
            <div className="bulk-edit-form">
              <div className="bulk-edit-info">
                <p>💡 <strong>Tip:</strong> Enter Done dates first. Due dates will auto-calculate (PM: +6 months - 1 day, Cal: +12 months - 1 day)</p>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>✅ PM Done Date:</label>
                  <div className="date-input-wrapper">
                    <input
                      type="text"
                      value={bulkEditForm.pmDone}
                      onChange={(e) => handleBulkEditChange('pmDone', e.target.value)}
                      placeholder="DD-Mon-YY or NA/NR"
                      className="text-date-input"
                    />
                    <input
                      type="date"
                      value={convertCustomToCalendarFormat(bulkEditForm.pmDone)}
                      onChange={(e) => handleBulkEditCalendarChange('pmDone', e.target.value)}
                      className="calendar-date-input"
                      title="Select date from calendar"
                    />
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('pmDone', 'NA')}
                      title="Mark as NA (Not Applicable)"
                    >
                      NA
                    </button>
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('pmDone', 'NR')}
                      title="Mark as NR (Not Required)"
                    >
                      NR
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>📅 PM Due Date: <span className="auto-label">(Auto)</span></label>
                  <div className="date-input-wrapper">
                    <input
                      type="text"
                      value={bulkEditForm.pmDue}
                      onChange={(e) => handleBulkEditChange('pmDue', e.target.value)}
                      placeholder="Auto-calculated or DD-Mon-YY or NA/NR"
                      className="auto-field text-date-input"
                    />
                    <input
                      type="date"
                      value={convertCustomToCalendarFormat(bulkEditForm.pmDue)}
                      onChange={(e) => handleBulkEditCalendarChange('pmDue', e.target.value)}
                      className="calendar-date-input"
                      title="Select date from calendar"
                    />
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('pmDue', 'NA')}
                      title="Mark as NA (Not Applicable)"
                    >
                      NA
                    </button>
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('pmDue', 'NR')}
                      title="Mark as NR (Not Required)"
                    >
                      NR
                    </button>
                  </div>
                  <small className="field-hint">Auto: Done + 182 days (6 months - 1 day)</small>
                </div>
                <div className="form-group">
                  <label>✅ Cal Done Date:</label>
                  <div className="date-input-wrapper">
                    <input
                      type="text"
                      value={bulkEditForm.calDone}
                      onChange={(e) => handleBulkEditChange('calDone', e.target.value)}
                      placeholder="DD-Mon-YY or NA/NR"
                      className="text-date-input"
                    />
                    <input
                      type="date"
                      value={convertCustomToCalendarFormat(bulkEditForm.calDone)}
                      onChange={(e) => handleBulkEditCalendarChange('calDone', e.target.value)}
                      className="calendar-date-input"
                      title="Select date from calendar"
                    />
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('calDone', 'NA')}
                      title="Mark as NA (Not Applicable)"
                    >
                      NA
                    </button>
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('calDone', 'NR')}
                      title="Mark as NR (Not Required)"
                    >
                      NR
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>📅 Cal Due Date: <span className="auto-label">(Auto)</span></label>
                  <div className="date-input-wrapper">
                    <input
                      type="text"
                      value={bulkEditForm.calDue}
                      onChange={(e) => handleBulkEditChange('calDue', e.target.value)}
                      placeholder="Auto-calculated or DD-Mon-YY or NA/NR"
                      className="auto-field text-date-input"
                    />
                    <input
                      type="date"
                      value={convertCustomToCalendarFormat(bulkEditForm.calDue)}
                      onChange={(e) => handleBulkEditCalendarChange('calDue', e.target.value)}
                      className="calendar-date-input"
                      title="Select date from calendar"
                    />
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('calDue', 'NA')}
                      title="Mark as NA (Not Applicable)"
                    >
                      NA
                    </button>
                    <button 
                      type="button"
                      className="btn-na-nr"
                      onClick={() => handleBulkEditChange('calDue', 'NR')}
                      title="Mark as NR (Not Required)"
                    >
                      NR
                    </button>
                  </div>
                  <small className="field-hint">Auto: Done + 364 days (1 year - 1 day)</small>
                </div>
              </div>
              <div className="bulk-edit-actions">
                <button 
                  className="btn btn-success"
                  onClick={applyBulkEdits}
                  disabled={selectedAssets.length === 0}
                >
                  Apply to {selectedAssets.length} Selected Assets
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={cancelBulkEditing}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="assets-section">
          <h3>Assets ({filteredAssets.length})</h3>
          
          <div className="table-container">
            <table className="assets-table">
              <thead>
                <tr>
                  {bulkEditMode && <th>Select</th>}
                  <th>Type</th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>Department</span>
                      <input
                        type="text"
                        className="column-filter-input"
                        placeholder="Search..."
                        value={columnFilters.department}
                        onChange={(e) => setColumnFilters({...columnFilters, department: e.target.value})}
                        list="department-options"
                      />
                      <datalist id="department-options">
                        {getUniqueValues('department').map(value => (
                          <option key={value} value={value} />
                        ))}
                      </datalist>
                    </div>
                  </th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>Equipment</span>
                      <input
                        type="text"
                        className="column-filter-input"
                        placeholder="Search..."
                        value={columnFilters.equipmentName}
                        onChange={(e) => setColumnFilters({...columnFilters, equipmentName: e.target.value})}
                        list="equipment-options"
                      />
                      <datalist id="equipment-options">
                        {getUniqueValues('equipmentName').map(value => (
                          <option key={value} value={value} />
                        ))}
                      </datalist>
                    </div>
                  </th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>Asset ID</span>
                      <input
                        type="text"
                        className="column-filter-input"
                        placeholder="Search..."
                        value={columnFilters.assetId}
                        onChange={(e) => setColumnFilters({...columnFilters, assetId: e.target.value})}
                        list="assetid-options"
                      />
                      <datalist id="assetid-options">
                        {getUniqueValues('assetId').map(value => (
                          <option key={value} value={value} />
                        ))}
                      </datalist>
                    </div>
                  </th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>Serial No</span>
                      <input
                        type="text"
                        className="column-filter-input"
                        placeholder="Search..."
                        value={columnFilters.serialNo}
                        onChange={(e) => setColumnFilters({...columnFilters, serialNo: e.target.value})}
                        list="serialno-options"
                      />
                      <datalist id="serialno-options">
                        {getUniqueValues('serialNo').map(value => (
                          <option key={value} value={value} />
                        ))}
                      </datalist>
                    </div>
                  </th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>PM Due</span>
                      <div className="date-range-filter">
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="From"
                          value={columnFilters.pmDue.start}
                          onChange={(e) => setColumnFilters({...columnFilters, pmDue: {...columnFilters.pmDue, start: e.target.value}})}
                          title="From date"
                        />
                        <span className="date-range-separator">to</span>
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="To"
                          value={columnFilters.pmDue.end}
                          onChange={(e) => setColumnFilters({...columnFilters, pmDue: {...columnFilters.pmDue, end: e.target.value}})}
                          title="To date"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>PM Done</span>
                      <div className="date-range-filter">
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="From"
                          value={columnFilters.pmDone.start}
                          onChange={(e) => setColumnFilters({...columnFilters, pmDone: {...columnFilters.pmDone, start: e.target.value}})}
                          title="From date"
                        />
                        <span className="date-range-separator">to</span>
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="To"
                          value={columnFilters.pmDone.end}
                          onChange={(e) => setColumnFilters({...columnFilters, pmDone: {...columnFilters.pmDone, end: e.target.value}})}
                          title="To date"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>Cal Due</span>
                      <div className="date-range-filter">
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="From"
                          value={columnFilters.calDue.start}
                          onChange={(e) => setColumnFilters({...columnFilters, calDue: {...columnFilters.calDue, start: e.target.value}})}
                          title="From date"
                        />
                        <span className="date-range-separator">to</span>
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="To"
                          value={columnFilters.calDue.end}
                          onChange={(e) => setColumnFilters({...columnFilters, calDue: {...columnFilters.calDue, end: e.target.value}})}
                          title="To date"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="th-filter-wrapper">
                      <span>Cal Done</span>
                      <div className="date-range-filter">
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="From"
                          value={columnFilters.calDone.start}
                          onChange={(e) => setColumnFilters({...columnFilters, calDone: {...columnFilters.calDone, start: e.target.value}})}
                          title="From date"
                        />
                        <span className="date-range-separator">to</span>
                        <input
                          type="date"
                          className="date-range-input"
                          placeholder="To"
                          value={columnFilters.calDone.end}
                          onChange={(e) => setColumnFilters({...columnFilters, calDone: {...columnFilters.calDone, end: e.target.value}})}
                          title="To date"
                        />
                      </div>
                    </div>
                  </th>
                  <th>Actions</th>
                  <th>Reports</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.length > 0 ? (
                  filteredAssets.map((asset, index) => {
                    // Additional safety check
                    if (!asset || typeof asset !== 'object') {
                      return null;
                    }
                    
                    // Determine maintenance type
                    const hasPM = asset.pmDue || asset.pmDone;
                    const hasCal = asset.calDue || asset.calDone;
                    let maintenanceType = '';
                    let typeClass = '';
                    
                    if (hasPM && hasCal) {
                      maintenanceType = 'PM + CAL';
                      typeClass = 'badge-both';
                    } else if (hasPM) {
                      maintenanceType = 'PM';
                      typeClass = 'badge-pm';
                    } else if (hasCal) {
                      maintenanceType = 'CAL';
                      typeClass = 'badge-cal';
                    }
                    
                    const isEditing = editingAsset && editingAsset.id === asset.id;
                    const isSelected = selectedAssets.includes(asset.id);
                    
                    return isEditing ? (
                      // Edit row
                      <tr key={asset.id || index} className="editing-row">
                        {bulkEditMode && <td></td>}
                        <td>
                          <span className={`maintenance-badge ${typeClass}`}>{maintenanceType}</span>
                        </td>
                        <td>{safeToString(asset.department)}</td>
                        <td>{safeToString(asset.equipmentName)}</td>
                        <td>{safeToString(asset.assetId)}</td>
                        <td>{safeToString(asset.serialNo)}</td>
                        <td>
                          <input
                            type="text"
                            value={editForm.pmDue}
                            onChange={(e) => handleEditChange('pmDue', e.target.value)}
                            placeholder="DD-Mon-YY or NA/NR"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editForm.pmDone}
                            onChange={(e) => handleEditChange('pmDone', e.target.value)}
                            placeholder="DD-Mon-YY or NA/NR"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editForm.calDue}
                            onChange={(e) => handleEditChange('calDue', e.target.value)}
                            placeholder="DD-Mon-YY or NA/NR"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editForm.calDone}
                            onChange={(e) => handleEditChange('calDone', e.target.value)}
                            placeholder="DD-Mon-YY or NA/NR"
                          />
                        </td>
                        <td>
                          <button className="btn btn-success btn-small" onClick={saveEditing}>
                            Save
                          </button>
                          <button className="btn btn-secondary btn-small" onClick={cancelEditing}>
                            Cancel
                          </button>
                        </td>
                        <td></td>
                      </tr>
                    ) : (
                      // Display row
                      <tr key={asset.id || index} className={isSelected ? 'selected-row' : ''}>
                        {bulkEditMode && (
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleAssetSelection(asset.id)}
                            />
                          </td>
                        )}
                        <td>
                          <span className={`maintenance-badge ${typeClass}`}>{maintenanceType}</span>
                        </td>
                        <td>{safeToString(asset.department)}</td>
                        <td>{safeToString(asset.equipmentName)}</td>
                        <td>{safeToString(asset.assetId)}</td>
                        <td>{safeToString(asset.serialNo)}</td>
                        <td>{formatAssetDate(asset.pmDue)}</td>
                        <td>{formatAssetDate(asset.pmDone)}</td>
                        <td>{formatAssetDate(asset.calDue)}</td>
                        <td>{formatAssetDate(asset.calDone)}</td>
                        <td>
                          <button 
                            className="btn btn-primary btn-small" 
                            onClick={() => startEditing(asset)}
                          >
                            Edit
                          </button>
                        </td>
                        <td>
                          <div className="report-actions">
                            <div className="report-status-indicators">
                              <span 
                                className={`report-status-badge ${asset.pmReportData ? 'uploaded' : 'missing'}`}
                                title={asset.pmReportData ? 'PM Report Uploaded' : 'PM Report Missing'}
                              >
                                PM {asset.pmReportData ? '✅' : '❌'}
                              </span>
                              <span 
                                className={`report-status-badge ${asset.calReportData ? 'uploaded' : 'missing'}`}
                                title={asset.calReportData ? 'Calibration Report Uploaded' : 'Calibration Report Missing'}
                              >
                                CAL {asset.calReportData ? '✅' : '❌'}
                              </span>
                            </div>
                            <button 
                              type="button"
                              className="btn btn-primary btn-small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Use setTimeout to prevent blocking the UI thread
                                setTimeout(() => {
                                  openReportUpload(asset);
                                }, 0);
                              }}
                              title="Upload Report"
                            >
                              📤 Upload
                            </button>
                            {/* Simple Eye Button - View Only */}
                            <button 
                              type="button"
                              className="btn btn-info btn-small eye-main-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openViewDocuments(asset);
                              }}
                              title="View Documents"
                            >
                              👁️ View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }).filter(row => row !== null)
                ) : (
                  <tr>
                    <td colSpan={bulkEditMode ? 12 : 11} className="no-data-cell">
                      No assets found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Report Upload Modal */}
        <ReportUploadModal
          isOpen={showReportUpload}
          onClose={closeReportUpload}
          asset={selectedAssetForReport}
          reportUploadForm={reportUploadForm}
          setReportUploadForm={setReportUploadForm}
          onSave={saveReports}
          onFileChange={handleReportFileChange}
          onStartCamera={startCamera}
          onRemovePage={removePage}
          onUpdateAssetDates={(assetId, field, value) => {
            // Update asset dates from ReportUploadModal
            const updatedAssets = assets.map(asset => {
              if (asset.id === assetId || asset.assetId === assetId) {
                return { ...asset, [field]: value };
              }
              return asset;
            });
            
            if (saveAssets(updatedAssets)) {
              // Update selected asset for report
              const updatedAsset = updatedAssets.find(a => a.id === assetId || a.assetId === assetId);
              if (updatedAsset) {
                setSelectedAssetForReport(updatedAsset);
                // Also update selectedAssetForView to keep ViewDocumentsModal in sync
                setSelectedAssetForView(updatedAsset);
              }
              console.log(`Asset ${assetId} ${field} updated to: ${value}`);
            }
          }}
        />
        
        {/* Camera Modal - Rendered as Portal */}
        {showCamera && ReactDOM.createPortal(
          <div 
            className="modal-overlay camera-overlay" 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 100000,
              padding: '1rem'
            }}
          >
            <div 
              className="modal-content camera-modal" 
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '0.5rem',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                position: 'relative'
              }}
            >
              <div className="modal-header">
                <h3>
                  {cameraPreview ? '👁️ Preview Photo' : '📷 Take Photo'} - {cameraForType === 'pm' ? 'PM Report' : 'Calibration Report'}
                </h3>
                <button className="btn-close" onClick={stopCamera}>×</button>
              </div>
              <div className="modal-body camera-body">
                {!cameraPreview ? (
                  /* Camera View */
                  <>
                    <div className="camera-container">
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline
                        className="camera-video"
                      />
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                    </div>
                    <div className="camera-controls">
                      <button className="btn btn-primary btn-capture" onClick={capturePhoto}>
                        📸 Capture Photo
                      </button>
                      <p className="camera-hint">Position the document and tap Capture</p>
                    </div>
                  </>
                ) : (
                  /* Preview View */
                  <>
                    <div className="camera-container" style={{ backgroundColor: '#000', padding: '1rem' }}>
                      <img 
                        src={cameraPreview} 
                        alt="Captured preview"
                        style={{
                          width: '100%',
                          maxHeight: '50vh',
                          objectFit: 'contain',
                          borderRadius: '0.5rem'
                        }}
                      />
                    </div>
                    <div className="camera-controls" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                      <button 
                        className="btn btn-secondary" 
                        onClick={retakePhoto}
                        style={{
                          padding: '0.75rem 1.5rem',
                          fontSize: '1rem',
                          backgroundColor: '#64748b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                      >
                        🔄 Retake
                      </button>
                      <button 
                        className="btn btn-success" 
                        onClick={savePhoto}
                        style={{
                          padding: '0.75rem 1.5rem',
                          fontSize: '1rem',
                          backgroundColor: '#22c55e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                      >
                        ✅ Save Photo
                      </button>
                    </div>
                    <p className="camera-hint">Review the photo. Click Save to add it or Retake to capture again.</p>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
        
        {/* Bulk Report Upload Modal */}
        {showBulkReportUpload && (
          <div className="modal-overlay" style={{
            contain: 'layout style paint',
            willChange: 'auto'
          }}>
            <div className="modal-content bulk-modal" style={{
              contain: 'layout style paint',
              willChange: 'auto',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transition: 'none',
              animation: 'none'
            }}>
              <div className="modal-header">
                <h3>📁 Bulk Upload PM/Calibration Reports</h3>
                <button className="btn-close" onClick={closeBulkReportUpload}>×</button>
              </div>
              <div className="modal-body" style={{ 
                  contain: 'layout style paint',
                  willChange: 'auto',
                  transform: 'translateZ(0)',
                  WebkitTransform: 'translateZ(0)',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  transition: 'none',
                  animation: 'none'
                }}>
                <div className="bulk-instructions">
                  <h4>📋 Naming Convention</h4>
                  <p>Name your files like:</p>
                  <ul>
                    <li><code>AssetID_PM.pdf</code> or <code>AssetID_CAL.jpg</code></li>
                    <li><code>SerialNo_PM.png</code> or <code>SerialNo_CAL.pdf</code></li>
                    <li>Examples: <code>EQ001_PM.pdf</code>, <code>SN123456_CAL.jpg</code></li>
                  </ul>
                  <p className="hint">Supported: JPG, PNG, PDF (Max 5MB each)</p>
                </div>
                
                <div className="file-upload-area bulk-upload-area" style={{
                    contain: 'layout style paint',
                    willChange: 'auto',
                    transform: 'translateZ(0)',
                    WebkitTransform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transition: 'none',
                    animation: 'none'
                  }}>
                  <input
                    type="file"
                    id="bulkReportFiles"
                    accept=".jpg,.jpeg,.png,.pdf"
                    multiple
                    onChange={(e) => handleBulkFileSelect(e.target.files)}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="bulkReportFiles" className="btn btn-primary btn-large">
                    📁 Select Multiple Files
                  </label>
                  <p className="file-hint">You can select multiple files at once</p>
                </div>
                
                {bulkReportFiles.length > 0 && (
                  <div className="selected-files-list">
                    <h4>📎 Selected Files ({bulkReportFiles.length})</h4>
                    <div className="files-scroll-container">
                      {bulkReportFiles.map((file, index) => (
                        <div key={index} className="file-item">
                          <span className="file-name">{file.name}</span>
                          <button 
                            className="btn-remove"
                            onClick={(e) => { e.stopPropagation(); removeBulkFile(index); }}
                            title="Remove file"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {isBulkUploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${(bulkUploadProgress.current / bulkUploadProgress.total) * 100}%` }}
                      ></div>
                    </div>
                    <p>Processing: {bulkUploadProgress.current} / {bulkUploadProgress.total}</p>
                    <p>Matched: {bulkUploadProgress.matched}</p>
                  </div>
                )}
                
                {bulkUploadProgress.unmatched.length > 0 && (
                  <div className="unmatched-files">
                    <h4>⚠️ Unmatched Files ({bulkUploadProgress.unmatched.length})</h4>
                    <div className="files-scroll-container">
                      {bulkUploadProgress.unmatched.map((item, index) => (
                        <div key={index} className="file-item unmatched">
                          <span className="file-name">{item.file}</span>
                          <span className="reason">{item.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Success/Error Message */}
                {bulkUploadSuccess && !bulkUploadSuccess.error && (
                  <div className="success-message" style={{ 
                    marginTop: '15px', 
                    padding: '12px 16px', 
                    backgroundColor: '#d4edda', 
                    border: '1px solid #c3e6cb', 
                    borderRadius: '6px',
                    color: '#155724'
                  }}>
                    <strong>✅ Upload Successful!</strong>
                    <p style={{ margin: '5px 0 0 0' }}>
                      Matched: {bulkUploadSuccess.matched} files | Unmatched: {bulkUploadSuccess.unmatched} files
                    </p>
                  </div>
                )}
                
                {bulkUploadSuccess && bulkUploadSuccess.error && (
                  <div className="error-message" style={{ 
                    marginTop: '15px', 
                    padding: '12px 16px', 
                    backgroundColor: '#f8d7da', 
                    border: '1px solid #f5c6cb', 
                    borderRadius: '6px',
                    color: '#721c24'
                  }}>
                    <strong>❌ Upload Failed</strong>
                    <p style={{ margin: '5px 0 0 0' }}>{bulkUploadSuccess.message}</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-success" 
                  onClick={processBulkUpload}
                  disabled={isBulkUploading || bulkReportFiles.length === 0}
                >
                  {isBulkUploading ? '⏳ Uploading...' : '💾 Start Bulk Upload'}
                </button>
                <button className="btn btn-secondary" onClick={closeBulkReportUpload}>
                  ❌ Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Smart Multi-Equipment Assignment Modal */}
        <SmartAssignModal
          isOpen={showSmartAssign}
          onClose={closeSmartAssign}
          assets={assets}
          onExecute={handleSmartAssignExecute}
          stream={stream}
          setStream={setStream}
        />
        
        {/* Bulk Upload Modal */}
        <BulkUploadModal
          isOpen={showBulkUpload}
          onClose={closeBulkUpload}
          assets={assets}
          onBulkUploadComplete={handleBulkUploadComplete}
        />
        
        {/* View Documents Modal - Rendered as Portal */}
        {showViewDocuments && selectedAssetForView && ReactDOM.createPortal(
          <div 
            className="modal-overlay" 
            onClick={(e) => {
              // Close modal when clicking on overlay (outside modal content)
              if (e.target === e.currentTarget) {
                closeViewDocuments();
              }
            }}
            style={{ pointerEvents: 'auto' }}
          >
            <div 
              className="modal-content view-docs-modal" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>📄 View Documents - {selectedAssetForView?.equipmentName || 'Unknown'}</h3>
                <button 
                  type="button"
                  className="btn-close" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeViewDocuments();
                  }}
                  style={{ 
                    position: 'relative', 
                    zIndex: 10000,
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {/* Report Type Tabs */}
                <div className="report-tabs">
                  <button 
                    className={`tab-btn ${currentReportType === 'pm' ? 'active' : ''}`}
                    onClick={() => { setCurrentReportType('pm'); setCurrentPage(0); setImageLoading(true); setImageError(false); }}
                    disabled={!selectedAssetForView || !hasReport(selectedAssetForView, 'pm')}
                  >
                    🔧 PM Report {selectedAssetForView && hasReport(selectedAssetForView, 'pm') ? `(${getReportPages(selectedAssetForView, 'pm').length} pages)` : '(No Data)'}
                  </button>
                  <button 
                    className={`tab-btn ${currentReportType === 'cal' ? 'active' : ''}`}
                    onClick={() => { setCurrentReportType('cal'); setCurrentPage(0); setImageLoading(true); setImageError(false); }}
                    disabled={!selectedAssetForView || !hasReport(selectedAssetForView, 'cal')}
                  >
                    ⚖️ Calibration Report {selectedAssetForView && hasReport(selectedAssetForView, 'cal') ? `(${getReportPages(selectedAssetForView, 'cal').length} pages)` : '(No Data)'}
                  </button>
                </div>

                {/* Report Info Bar - File Info and Service Date */}
                {selectedAssetForView && hasReport(selectedAssetForView, currentReportType) && (
                  <div className="report-info-bar" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', backgroundColor: '#f8fafc', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}>
                    <div className="report-info" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span>
                        <strong>File:</strong>{' '}
                        {(() => {
                          // Generate proper file name according to naming convention
                          // Format: EquipmentName_AssetID_SN_Number_PM_DDMMYYYY.ext
                          const equipmentName = selectedAssetForView.equipmentName?.replace(/\s+/g, '_') || 'Equipment';
                          const assetId = selectedAssetForView.assetId || 'Unknown';
                          const serialNumber = selectedAssetForView.serialNumber || 'SN_Unknown';
                          const reportType = currentReportType.toUpperCase();
                          
                          // Get service date based on report type
                          const serviceDate = currentReportType === 'pm' 
                            ? selectedAssetForView.pmDone 
                            : selectedAssetForView.calDone;
                          
                          // Format date as DDMMYYYY
                          let dateStr = '';
                          // Convert to string if it's a Date object or other type
                          const serviceDateStr = serviceDate ? String(serviceDate) : '';
                          if (serviceDateStr && serviceDateStr !== 'NA' && serviceDateStr !== 'NR') {
                            // Parse DD-Mon-YYYY format
                            const dateMatch = serviceDateStr.match(/(\d{2})-([A-Za-z]{3})-(\d{4})/);
                            if (dateMatch) {
                              const day = dateMatch[1];
                              const monthMap = {
                                'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
                                'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
                                'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
                              };
                              const month = monthMap[dateMatch[2]] || '01';
                              const year = dateMatch[3];
                              dateStr = `${day}${month}${year}`;
                            }
                          }
                          
                          // Get original file extension or default to pdf
                          const originalFileName = currentReportType === 'pm'
                            ? selectedAssetForView.pmReportFileName
                            : selectedAssetForView.calReportFileName;
                          const extMatch = originalFileName?.match(/\.([^.]+)$/);
                          const ext = extMatch ? extMatch[1] : 'pdf';
                          
                          // Generate formatted file name
                          const formattedFileName = dateStr 
                            ? `${equipmentName}_${assetId}_${serialNumber}_${reportType}_${dateStr}.${ext}`
                            : `${equipmentName}_${assetId}_${serialNumber}_${reportType}.${ext}`;
                          
                          return formattedFileName;
                        })()}
                      </span>
                      <span style={{ color: '#94a3b8' }}>|</span>
                      <span>
                        <strong>Pages:</strong>{' '}
                        {getReportPages(selectedAssetForView, currentReportType).length}
                      </span>
                      <span style={{ color: '#94a3b8' }}>|</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <strong>{currentReportType === 'pm' ? 'PM' : 'Calibration'} Service Date:</strong>
                        <select
                          value={currentReportType === 'pm' 
                            ? (editingDates.pmDone === 'NA' || editingDates.pmDone === 'NR' ? editingDates.pmDone : 'DATE')
                            : (editingDates.calDone === 'NA' || editingDates.calDone === 'NR' ? editingDates.calDone : 'DATE')
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            const newDoneDate = value === 'DATE' ? '' : value;
                            const newDueDate = value === 'DATE' ? '' : value;
                            
                            setEditingDates(prev => ({
                              ...prev,
                              [currentReportType === 'pm' ? 'pmDone' : 'calDone']: newDoneDate,
                              [currentReportType === 'pm' ? 'pmDue' : 'calDue']: newDueDate
                            }));
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #cbd5e1',
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem',
                            color: '#334155'
                          }}
                        >
                          <option value="DATE">Select Date</option>
                          <option value="NA">NA</option>
                          <option value="NR">NR</option>
                        </select>
                        {(currentReportType === 'pm' 
                          ? (editingDates.pmDone !== 'NA' && editingDates.pmDone !== 'NR')
                          : (editingDates.calDone !== 'NA' && editingDates.calDone !== 'NR')
                        ) && (
                          <input
                            type="date"
                            value={currentReportType === 'pm' ? editingDates.pmDone : editingDates.calDone}
                            onChange={(e) => {
                              const newDate = e.target.value; // YYYY-MM-DD format
                              
                              // Calculate due date in YYYY-MM-DD format
                              let dueDate = '';
                              if (newDate) {
                                const doneDateObj = new Date(newDate);
                                if (!isNaN(doneDateObj.getTime())) {
                                  const dueDateObj = new Date(doneDateObj);
                                  dueDateObj.setMonth(dueDateObj.getMonth() + (currentReportType === 'pm' ? 6 : 12));
                                  dueDateObj.setDate(dueDateObj.getDate() - 1);
                                  dueDate = dueDateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
                                }
                              }
                              
                              setEditingDates(prev => ({
                                ...prev,
                                [currentReportType === 'pm' ? 'pmDone' : 'calDone']: newDate,
                                [currentReportType === 'pm' ? 'pmDue' : 'calDue']: dueDate
                              }));
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: '1px solid #cbd5e1',
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem',
                              color: '#334155'
                            }}
                          />
                        )}
                      </span>
                      <span style={{ color: '#94a3b8' }}>|</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <strong>{currentReportType === 'pm' ? 'PM' : 'Calibration'} Due Date:</strong>
                        <select
                          value={currentReportType === 'pm' 
                            ? (editingDates.pmDue === 'NA' || editingDates.pmDue === 'NR' ? editingDates.pmDue : 'DATE')
                            : (editingDates.calDue === 'NA' || editingDates.calDue === 'NR' ? editingDates.calDue : 'DATE')
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            const newDueDate = value === 'DATE' ? '' : value;
                            
                            setEditingDates(prev => ({
                              ...prev,
                              [currentReportType === 'pm' ? 'pmDue' : 'calDue']: newDueDate
                            }));
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #cbd5e1',
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem',
                            color: '#334155'
                          }}
                        >
                          <option value="DATE">Select Date</option>
                          <option value="NA">NA</option>
                          <option value="NR">NR</option>
                        </select>
                        {(currentReportType === 'pm' 
                          ? (editingDates.pmDue !== 'NA' && editingDates.pmDue !== 'NR')
                          : (editingDates.calDue !== 'NA' && editingDates.calDue !== 'NR')
                        ) && (
                          <input
                            type="date"
                            value={currentReportType === 'pm' ? editingDates.pmDue : editingDates.calDue}
                            onChange={(e) => {
                              const newDueDate = e.target.value;
                              setEditingDates(prev => ({
                                ...prev,
                                [currentReportType === 'pm' ? 'pmDue' : 'calDue']: newDueDate
                              }));
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: '1px solid #cbd5e1',
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem',
                              color: '#334155'
                            }}
                          />
                        )}
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          (Auto: {currentReportType === 'pm' ? '+6 months - 1 day' : '+12 months - 1 day'})
                        </span>
                      </span>
                      <span style={{ color: '#94a3b8' }}>|</span>
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => {
                          // Get the edited dates for current report type
                          const doneDateRaw = currentReportType === 'pm' ? editingDates.pmDone : editingDates.calDone;
                          const dueDateRaw = currentReportType === 'pm' ? editingDates.pmDue : editingDates.calDue;
                          
                          // Convert dates to DD-Mon-YYYY format for storage (or keep NA/NR)
                          const doneDate = doneDateRaw === 'NA' || doneDateRaw === 'NR' 
                            ? doneDateRaw 
                            : convertCalendarToCustomFormat(doneDateRaw);
                          const dueDate = dueDateRaw === 'NA' || dueDateRaw === 'NR' 
                            ? dueDateRaw 
                            : convertCalendarToCustomFormat(dueDateRaw);
                          
                          // Update the asset in the assets array
                          const updatedAssets = assets.map(asset => {
                            if (asset.id === selectedAssetForView.id || asset.assetId === selectedAssetForView.assetId) {
                              return {
                                ...asset,
                                [currentReportType === 'pm' ? 'pmDone' : 'calDone']: doneDate,
                                [currentReportType === 'pm' ? 'pmDue' : 'calDue']: dueDate
                              };
                            }
                            return asset;
                          });
                          
                          // Save to localStorage
                          if (saveAssets(updatedAssets)) {
                            // Update the selected asset for view
                            const updatedAsset = updatedAssets.find(a => a.id === selectedAssetForView.id || a.assetId === selectedAssetForView.assetId);
                            if (updatedAsset) {
                              setSelectedAssetForView(updatedAsset);
                              // Also update selectedAssetForReport to keep ReportUploadModal in sync
                              setSelectedAssetForReport(updatedAsset);
                            }
                            
                            // Show success message
                            setDateSaveMessage(`${currentReportType === 'pm' ? 'PM' : 'Calibration'} dates saved successfully!`);
                            setTimeout(() => setDateSaveMessage(''), 3000);
                            
                            console.log(`${currentReportType === 'pm' ? 'PM' : 'Calibration'} dates saved - Service: ${doneDate}, Due: ${dueDate}`);
                          }
                        }}
                        style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        💾 Save Dates
                      </button>
                      {dateSaveMessage && (
                        <span style={{ 
                          color: '#16a34a', 
                          fontSize: '0.875rem', 
                          fontWeight: '600',
                          animation: 'fadeIn 0.3s ease'
                        }}>
                          ✅ {dateSaveMessage}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Document Viewer */}
                <div className="document-viewer">
                  {selectedAssetForView && hasReport(selectedAssetForView, currentReportType) ? (
                    <div className="document-container">
                      <div className="page-info">
                        Page {currentPage + 1} of {getReportPages(selectedAssetForView, currentReportType).length}
                      </div>
                      <div className="document-image-container">
                        {getReportPages(selectedAssetForView, currentReportType)[currentPage] ? (
                          <>
                            {(() => {
                              const pageData = getReportPages(selectedAssetForView, currentReportType)[currentPage];
                              
                              // Validate page data
                              if (!pageData || typeof pageData !== 'string') {
                                return (
                                  <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center',
                                    backgroundColor: '#fef2f2',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #fecaca'
                                  }}>
                                    <span style={{ fontSize: '3rem' }}>⚠️</span>
                                    <p style={{ color: '#dc2626', margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>Invalid document data</p>
                                    <p style={{ color: '#7f1d1d', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
                                      The document data is corrupted or missing.
                                    </p>
                                  </div>
                                );
                              }
                              
                              // Handle storage limited case
                              if (pageData === '[STORAGE_LIMITED]') {
                                return (
                                  <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center',
                                    backgroundColor: '#fef3c7',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #fcd34d'
                                  }}>
                                    <span style={{ fontSize: '3rem' }}>📄</span>
                                    <p style={{ color: '#92400e', margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>Report Archived</p>
                                    <p style={{ color: '#a16207', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
                                      This report has been archived to save storage space.
                                    </p>
                                  </div>
                                );
                              }
                              
                              // Check if data is a valid data URL or http URL
                              const isValidDataUrl = pageData.startsWith('data:') || pageData.startsWith('http');
                              if (!isValidDataUrl) {
                                console.error('Invalid data URL format. Data starts with:', pageData.substring(0, 50));
                                return (
                                  <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center',
                                    backgroundColor: '#fef2f2',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #fecaca'
                                  }}>
                                    <span style={{ fontSize: '3rem' }}>⚠️</span>
                                    <p style={{ color: '#dc2626', margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>Invalid Image Format</p>
                                    <p style={{ color: '#7f1d1d', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
                                      The image data format is not valid.
                                    </p>
                                  </div>
                                );
                              }
                              
                              const isPdf = pageData.startsWith('data:application/pdf');
                              
                              if (isPdf) {
                                return (
                                  <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center',
                                    backgroundColor: '#f1f5f9',
                                    borderRadius: '0.5rem'
                                  }}>
                                    <span style={{ fontSize: '3rem' }}>📄</span>
                                    <p style={{ margin: '1rem 0', color: '#64748b' }}>PDF Document</p>
                                    <a
                                      href={pageData}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      download={`${currentReportType.toUpperCase()}_Report_Page_${currentPage + 1}.pdf`}
                                      style={{
                                        display: 'inline-block',
                                        padding: '0.5rem 1rem',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        borderRadius: '0.375rem',
                                        textDecoration: 'none',
                                        fontSize: '0.875rem',
                                        marginTop: '0.5rem'
                                      }}
                                    >
                                      👁️ View / Download PDF
                                    </a>
                                  </div>
                                );
                              }
                              
                              return (
                                <>
                                  {imageError ? (
                                    <div style={{ 
                                      padding: '2rem', 
                                      textAlign: 'center',
                                      backgroundColor: '#fef2f2',
                                      borderRadius: '0.5rem',
                                      border: '1px solid #fecaca'
                                    }}>
                                      <span style={{ fontSize: '3rem' }}>⚠️</span>
                                      <p style={{ color: '#dc2626', margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>Failed to load document image</p>
                                      <p style={{ color: '#7f1d1d', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
                                        The image data may be corrupted or unavailable.
                                      </p>
                                      <button
                                        onClick={() => {
                                          setImageError(false);
                                          // Force image reload by clearing and resetting
                                          const img = document.querySelector('.document-image');
                                          if (img) {
                                            img.src = pageData;
                                          }
                                        }}
                                        style={{
                                          marginTop: '1rem',
                                          padding: '0.5rem 1rem',
                                          backgroundColor: '#3b82f6',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '0.375rem',
                                          cursor: 'pointer',
                                          fontSize: '0.875rem'
                                        }}
                                      >
                                        🔄 Retry
                                      </button>
                                    </div>
                                  ) : (
                                    <img 
                                      key={`${currentReportType}-${currentPage}-${Date.now()}`}
                                      src={pageData} 
                                      alt={`${currentReportType.toUpperCase()} Report Page ${currentPage + 1}`}
                                      className="document-image"
                                      loading="eager"
                                      decoding="sync"
                                      style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                                      onLoad={() => setImageLoading(false)}
                                      onError={(e) => {
                                        console.error('Image failed to load. Data length:', pageData?.length, 'Data starts with:', pageData?.substring(0, 100));
                                        setImageLoading(false);
                                        setImageError(true);
                                      }}
                                    />
                                  )}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <p style={{ padding: '20px', color: '#666' }}>⚠️ No image data available</p>
                        )}
                      </div>
                      {/* Page Navigation */}
                      {getReportPages(selectedAssetForView, currentReportType).length > 1 && (
                        <div className="page-navigation">
                          <button 
                            className="btn btn-secondary"
                            onClick={() => { setImageLoading(true); setImageError(false); setCurrentPage(prev => Math.max(0, prev - 1)); }}
                            disabled={currentPage === 0}
                          >
                            ← Previous
                          </button>
                          <span className="page-indicator">
                            {currentPage + 1} / {getReportPages(selectedAssetForView, currentReportType).length}
                          </span>
                          <button 
                            className="btn btn-secondary"
                            onClick={() => { setImageLoading(true); setImageError(false); setCurrentPage(prev => Math.min(getReportPages(selectedAssetForView, currentReportType).length - 1, prev + 1)); }}
                            disabled={currentPage >= getReportPages(selectedAssetForView, currentReportType).length - 1}
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-document">
                      <p>📭 No {currentReportType === 'pm' ? 'PM' : 'Calibration'} report available</p>
                      <button 
                        className="btn btn-primary"
                        onClick={() => {
                          closeViewDocuments();
                          openReportUpload(selectedAssetForView);
                        }}
                      >
                        📤 Upload Report
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' }}>
                {hasReport(selectedAssetForView, currentReportType) && (
                  <>
                    <button 
                      className="btn btn-secondary btn-small"
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        const pageData = getCurrentReportPageData(selectedAssetForView, currentReportType, currentPage);
                        if (!printWindow || !pageData) return;

                        const isPdf = pageData.startsWith('data:application/pdf');
                        printWindow.document.write(`
                          <html>
                            <head>
                              <meta charset="utf-8">
                              <title>Print ${currentReportType.toUpperCase()} Report</title>
                              <style>
                                body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #fff; }
                                img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                                .pdf-frame { width: 100%; height: 100vh; border: none; }
                                @media print { body { margin: 0; } img { width: 100%; height: auto; } }
                              </style>
                            </head>
                            <body>
                              ${isPdf
                                ? `<embed src="${pageData}" type="application/pdf" class="pdf-frame" />`
                                : `<img src="${pageData}" onload="window.print(); window.onfocus = function() { window.close(); }" />`
                              }
                              ${isPdf
                                ? `<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 700); };</script>`
                                : ''
                              }
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                      }}
                      title="Print Report"
                    >
                      🖨️ Print
                    </button>
                    <button 
                      className="btn btn-success btn-small"
                      onClick={() => {
                        const reportData = currentReportType === 'pm' 
                          ? selectedAssetForView.pmReportData 
                          : selectedAssetForView.calReportData;
                        const fileName = currentReportType === 'pm'
                          ? (selectedAssetForView.pmReportFileName || `PM_Report_${selectedAssetForView.assetId}.pdf`)
                          : (selectedAssetForView.calReportFileName || `Cal_Report_${selectedAssetForView.assetId}.pdf`);
                        if (reportData) {
                          const link = document.createElement('a');
                          link.href = reportData;
                          link.download = fileName;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }
                      }}
                      title="Download Report"
                    >
                      📥 Download
                    </button>
                    <button 
                      className="btn btn-info btn-small"
                      onClick={() => {
                        const history = [];
                        const reportType = currentReportType === 'pm' ? 'PM' : 'Calibration';
                        const uploadDate = currentReportType === 'pm'
                          ? (selectedAssetForView.pmReportUploadDate || 'N/A')
                          : (selectedAssetForView.calReportUploadDate || 'N/A');
                        const fileName = currentReportType === 'pm'
                          ? (selectedAssetForView.pmReportFileName || 'N/A')
                          : (selectedAssetForView.calReportFileName || 'N/A');
                        // Get PM/Cal done date from asset
                        const doneDate = currentReportType === 'pm'
                          ? (selectedAssetForView.pmDone || 'N/A')
                          : (selectedAssetForView.calDone || 'N/A');
                        
                        history.push({
                          date: uploadDate,
                          action: 'Current Report',
                          fileName: fileName,
                          type: reportType,
                          doneDate: doneDate
                        });
                        
                        if (selectedAssetForView.lastModified && selectedAssetForView.lastModified !== uploadDate) {
                          history.push({
                            date: selectedAssetForView.lastModified,
                            action: 'Asset Modified',
                            fileName: 'N/A',
                            type: reportType,
                            doneDate: doneDate
                          });
                        }
                        
                        setReportHistoryData(history);
                        setShowReportHistory(true);
                      }}
                      title="View Report History"
                    >
                      📜 History
                    </button>
                    <label className="btn btn-warning btn-small file-replace-btn" title="Replace Report" style={{ cursor: 'pointer' }}>
                      🔄 Replace
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleReplaceReport(selectedAssetForView, currentReportType, e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                    <button 
                      className="btn btn-danger btn-small"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete this ${currentReportType === 'pm' ? 'PM' : 'Calibration'} report?`)) {
                          deleteReport(selectedAssetForView, currentReportType);
                        }
                      }}
                      title="Remove Report"
                    >
                      🗑️ Remove
                    </button>
                  </>
                )}
                <button 
                  type="button"
                  className="btn btn-secondary btn-small" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeViewDocuments();
                  }}
                >
                  ❌ Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        
        {/* Report History Modal - Rendered as Portal */}
        {showReportHistory && ReactDOM.createPortal(
          <div 
            className="modal-overlay" 
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowReportHistory(false);
              }
            }}
            style={{ pointerEvents: 'auto', zIndex: 100001 }}
          >
            <div 
              className="modal-content report-history-modal" 
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '900px', maxHeight: '90vh' }}
            >
              <div className="modal-header">
                <h3>📜 Report History - Audit Trail</h3>
                <button 
                  type="button"
                  className="btn-close" 
                  onClick={() => setShowReportHistory(false)}
                  style={{ position: 'relative', zIndex: 10000, cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              {/* Asset Info Bar - Shows PM/Cal Done Date */}
              {selectedAssetForView && (
                <div className="report-info-bar" style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: '#f0f9ff', 
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  <div className="report-info" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                      {selectedAssetForView.equipmentName || selectedAssetForView.name || 'Unknown Equipment'}
                    </span>
                    <span style={{ color: '#64748b' }}>|</span>
                    <span style={{ color: '#475569' }}>
                      Asset ID: <strong>{selectedAssetForView.assetId || 'N/A'}</strong>
                    </span>
                    <span style={{ color: '#64748b' }}>|</span>
                    <span style={{ color: '#475569' }}>
                      S/N: <strong>{selectedAssetForView.serialNo || 'N/A'}</strong>
                    </span>
                  </div>
                  <div className="report-date-info" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    backgroundColor: '#dcfce7',
                    padding: '0.375rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #86efac'
                  }}>
                    <span style={{ fontSize: '1rem' }}>✅</span>
                    <span style={{ color: '#166534', fontWeight: '600' }}>
                      {currentReportType === 'pm' ? 'PM' : 'Calibration'} Done: 
                    </span>
                    <span style={{ color: '#15803d', fontWeight: '700' }}>
                      {currentReportType === 'pm' 
                        ? (selectedAssetForView.pmDone ? formatAssetDate(selectedAssetForView.pmDone) : 'Not Set')
                        : (selectedAssetForView.calDone ? formatAssetDate(selectedAssetForView.calDone) : 'Not Set')
                      }
                    </span>
                  </div>
                </div>
              )}
              <div className="modal-body" style={{ maxHeight: 'calc(90vh - 140px)', overflowY: 'auto', padding: '1.5rem' }}>
                {/* Current Report Section */}
                {selectedAssetForView && hasReport(selectedAssetForView, currentReportType) && (
                  <div className="current-report-section" style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                      backgroundColor: '#eff6ff',
                      border: '2px solid #3b82f6',
                      borderRadius: '0.5rem',
                      padding: '1rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: '1rem'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            color: '#1d4ed8',
                            fontWeight: '700',
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            marginBottom: '0.5rem'
                          }}>
                            CURRENT REPORT
                          </div>
                          <div style={{ color: '#374151', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                            <strong>File:</strong> {currentReportType === 'pm' 
                              ? (selectedAssetForView.pmReportFileName || 'PM_Report.pdf')
                              : (selectedAssetForView.calReportFileName || 'Calibration_Report.pdf')
                            }
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            <strong>Uploaded:</strong> {currentReportType === 'pm' 
                              ? (selectedAssetForView.pmReportUploadDate ? formatAssetDate(selectedAssetForView.pmReportUploadDate) : 'N/A')
                              : (selectedAssetForView.calReportUploadDate ? formatAssetDate(selectedAssetForView.calReportUploadDate) : 'N/A')
                            }
                          </div>
                          {(currentReportType === 'pm' ? selectedAssetForView.pmDone : selectedAssetForView.calDone) && (
                            <div style={{ color: '#16a34a', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              <strong>✅ {currentReportType === 'pm' ? 'PM' : 'Calibration'} Done:</strong> {formatAssetDate(currentReportType === 'pm' ? selectedAssetForView.pmDone : selectedAssetForView.calDone)}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const reportData = getCurrentReportPageData(selectedAssetForView, currentReportType, currentPage);
                            const fileName = currentReportType === 'pm' 
                              ? (selectedAssetForView.pmReportFileName || 'PM_Report.pdf')
                              : (selectedAssetForView.calReportFileName || 'Calibration_Report.pdf');
                            
                            const newWindow = window.open('', '_blank');
                            if (!newWindow || !reportData) return;
                            const isPdf = reportData.startsWith('data:application/pdf');
                            
                            if (isPdf) {
                              newWindow.document.write(`
                                <html>
                                  <head><title>${fileName}</title></head>
                                  <body style="margin: 0;">
                                    <iframe src="${reportData}" style="width: 100%; height: 100vh; border: none;"></iframe>
                                  </body>
                                </html>
                              `);
                            } else {
                              newWindow.document.write(`
                                <html>
                                  <head><title>${fileName}</title></head>
                                  <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f1f5f9;">
                                    <img src="${reportData}" alt="${fileName}" style="max-width: 100%; max-height: 100vh; object-fit: contain;" />
                                  </body>
                                </html>
                              `);
                            }
                            newWindow.document.close();
                          }}
                          style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.375rem',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          👁️ View
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Previous Reports Section */}
                {selectedAssetForView && (
                  <div className="previous-reports-section">
                    <h4 style={{ 
                      color: '#1e293b', 
                      fontSize: '1rem', 
                      fontWeight: '600',
                      marginBottom: '1rem'
                    }}>
                      Previous Reports:
                    </h4>
                    
                    {(() => {
                      const history = currentReportType === 'pm' 
                        ? (selectedAssetForView.pmReportHistory || [])
                        : (selectedAssetForView.calReportHistory || []);
                      
                      if (history.length === 0) {
                        return (
                          <div style={{ 
                            padding: '1rem', 
                            backgroundColor: '#f9fafb', 
                            borderRadius: '0.375rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '0.875rem'
                          }}>
                            📭 No old reports in archive. When you replace a report, the old one will be saved here.
                          </div>
                        );
                      }
                      
                      return (
                        <div className="previous-reports-list">
                          {history.slice().reverse().map((oldReport, index) => {
                            const reportNumber = history.length - index;
                            const historyPages = getHistoryReportPages(oldReport);
                            const firstHistoryPage = historyPages[0] || null;
                            const isStorageLimited = oldReport.reportData === '[STORAGE_LIMITED]' || oldReport.data === '[STORAGE_LIMITED]';
                            const hasViewableDocument = !!firstHistoryPage && firstHistoryPage !== '[STORAGE_LIMITED]';
                            
                            return (
                              <div 
                                key={index}
                                style={{
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '0.5rem',
                                  padding: '1rem',
                                  marginBottom: '1rem'
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  flexWrap: 'wrap',
                                  gap: '1rem'
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{
                                      color: '#1e293b',
                                      fontWeight: '700',
                                      fontSize: '1rem',
                                      marginBottom: '0.5rem'
                                    }}>
                                      Report #{reportNumber}
                                    </div>
                                    <div style={{ color: '#4b5563', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                      <strong>File:</strong> {oldReport.fileName}
                                    </div>
                                    <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                      <strong>Originally uploaded:</strong> {oldReport.date && oldReport.date !== 'N/A' ? formatAssetDate(oldReport.date) : 'N/A'}
                                    </div>
                                    {oldReport.doneDate && oldReport.doneDate !== 'N/A' && (
                                      <div style={{ color: '#16a34a', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                        <strong>✅ {currentReportType === 'pm' ? 'PM' : 'Cal'} Done:</strong> {formatAssetDate(oldReport.doneDate)}
                                      </div>
                                    )}
                                    <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                                      <strong>Archived:</strong> {oldReport.archivedDate ? formatAssetDate(oldReport.archivedDate) : (oldReport.date ? formatAssetDate(oldReport.date) : 'N/A')}
                                    </div>
                                  </div>
                                  
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (isStorageLimited || !hasViewableDocument) {
                                        alert('This report has been archived due to storage limits. Only metadata (filename and date) was preserved.');
                                        return;
                                      }
                                      const newWindow = window.open('', '_blank');
                                      if (!newWindow) {
                                        alert('Popup blocked. Please allow popups for this site.');
                                        return;
                                      }
                                      const isPdf = firstHistoryPage && firstHistoryPage.startsWith('data:application/pdf');
                                      
                                      if (isPdf) {
                                        newWindow.document.write(`
                                          <html>
                                            <head><title>${oldReport.fileName}</title></head>
                                            <body style="margin: 0;">
                                              <iframe src="${firstHistoryPage}" style="width: 100%; height: 100vh; border: none;"></iframe>
                                            </body>
                                          </html>
                                        `);
                                      } else {
                                        newWindow.document.write(`
                                          <html>
                                            <head><title>${oldReport.fileName}</title></head>
                                            <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f1f5f9;">
                                              <img src="${firstHistoryPage}" alt="${oldReport.fileName}" style="max-width: 100%; max-height: 100vh; object-fit: contain;" />
                                            </body>
                                          </html>
                                        `);
                                      }
                                      newWindow.document.close();
                                    }}
                                    style={{
                                      backgroundColor: isStorageLimited ? '#9ca3af' : '#3b82f6',
                                      color: 'white',
                                      padding: '0.5rem 1rem',
                                      borderRadius: '0.375rem',
                                      border: 'none',
                                      cursor: isStorageLimited ? 'not-allowed' : 'pointer',
                                      fontSize: '0.875rem',
                                      fontWeight: '600',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    👁️ View
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => setShowReportHistory(false)}
                >
                  ❌ Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default PMCalibrationTracker;